import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, PencilSimple, FloppyDisk, X, Plus, Trash, PaperPlaneTilt, Clock, Envelope, MagnifyingGlass, CheckCircle, ChatCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import HtmlEditor from "@/components/HtmlEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/AuthContext";
import { deriveLeadTimezone, getTzAbbrev, formatInTz } from "@/lib/leadTimezone";
import { formatTz, resolveTz, formatLocal, formatLocalDate } from "@/lib/timezone";
import TimezoneSelect from "@/components/TimezoneSelect";
import { toast } from "sonner";
import { LEAD_MODULES } from "./LeadsList";
import { safeSrcDoc } from "@/lib/safeHtml";
import RichTextEditor from "@/pages/marketing/RichTextEditor";
import ExternallySentEmailsTab from "@/components/ExternallySentEmailsTab";
import BccAddressCopy from "@/components/BccAddressCopy";

const LEAD_CATEGORIES = ["Initial Contact", "Demo Scheduled", "Proposal Sent", "Negotiation", "Closed Won", "Closed Lost", "On Hold"];
const CAT_COLORS = {
  "Initial Contact": "bg-blue-50 text-blue-700 border-blue-200",
  "Demo Scheduled": "bg-purple-50 text-purple-700 border-purple-200",
  "Proposal Sent": "bg-amber-50 text-amber-700 border-amber-200",
  "Negotiation": "bg-orange-50 text-orange-700 border-orange-200",
  "Closed Won": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Closed Lost": "bg-red-50 text-red-700 border-red-200",
  "On Hold": "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]",
};
const EMAIL_STATUS = { Sent: "bg-emerald-50 text-emerald-700", Pending: "bg-amber-50 text-amber-700", Scheduled: "bg-blue-50 text-blue-700", Cancelled: "bg-[#F5F5F5] text-[#A3A3A3]" };
const SOURCES = ["LinkedIn", "Referral", "Website", "Cold Outreach", "Conference", "Partner", "Other"];
const inputCls = "w-full border border-[#E5E5E5] px-3 py-1.5 text-sm focus:outline-none focus:border-[#0055FF]";
// Cells got tighter in Session 1.14b — when a 360px Event Log sidebar
// pins to the right, the form fields lose ~360px of horizontal real
// estate. Label width 140→110 and px-3→px-2.5 reclaims most of that
// without touching the visual hierarchy.
const cellLabel = "text-[10px] text-[#A3A3A3] uppercase tracking-wider font-bold py-1.5 px-2.5 bg-[#FAFAFA] border border-[#F5F5F5] w-[110px] align-top";
const cellValue = "text-sm text-[#0A0A0A] py-1.5 px-2.5 border border-[#F5F5F5] align-top break-words";

function TabBtn({ id, label, active, onClick, count }) {
  return (
    <button onClick={() => onClick(id)} data-testid={`lead-tab-${id}`}
      className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${active ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#A3A3A3] hover:text-[#525252]"}`}>
      {label}{count > 0 && <span className="ml-1.5 bg-[#F5F5F5] text-[#525252] text-[10px] px-1.5 py-0.5">{count}</span>}
    </button>
  );
}

// Event log — historically rendered inside the tab bar, but as of
// Session 1.14b it's pinned as a permanent right-rail sidebar so reps
// see activity context without clicking. The component is rendered in a
// narrow column (~360px) so we lean on `break-words` for long URLs and
// system-generated lines (e.g. forwarded webhook bodies).
function EventsTab({ events }) {
  if (!events?.length) return <div className="p-6 text-center text-[#A3A3A3] text-sm">No events logged yet</div>;
  return (
    <div className="divide-y divide-[#F5F5F5] overflow-y-auto max-h-[calc(100vh-220px)] min-w-0" data-testid="events-tab-content">
      {events.map(evt => (
        <div key={evt.event_id} className="px-3 py-2.5 flex items-start gap-2 min-w-0">
          <div className="w-1.5 h-1.5 rounded-full bg-[#0055FF] mt-2 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[#525252] break-words whitespace-pre-wrap leading-snug">{evt.description}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-[10px] text-[#A3A3A3] font-mono">{evt.created_at ? formatLocal(evt.created_at) : ""}</span>
              {evt.user_name && <span className="text-[10px] text-[#A3A3A3] truncate">{evt.user_name}</span>}
              <span className={`text-[10px] px-1.5 py-0.5 font-bold ${evt.event_type === "automated" ? "bg-blue-50 text-blue-600" : "bg-[#F5F5F5] text-[#A3A3A3]"}`}>{evt.event_type}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


// v1.67 — Lifecycle tab. Filters the lead's event timeline down to
// status-changing events (pause / resume / bounce / unsubscribe /
// enrol / status change) and headlines the lead's CURRENT state
// (Paused + reason, Inactive + status, Active + last activity).
// Answers Mike's "how did this lead get here?" question on one screen.
const LIFECYCLE_EVENT_TYPES = new Set([
  "automated", "pause", "resume", "unsubscribed", "bounced",
  "enrolled", "unenrolled", "booking_inquiry_sent",
  "booking_auto_resumed", "replied", "quarantine_applied",
  "status_change", "manual",
]);
const LIFECYCLE_KEYWORDS = [
  "paused", "resumed", "bounce", "unsubscrib", "enroll",
  "quarantin", "do not contact", "booking", "reply",
];

function LifecycleTab({ lead, events }) {
  const lifecycleEvents = (events || []).filter(e => {
    if (LIFECYCLE_EVENT_TYPES.has((e.event_type || "").toLowerCase())) return true;
    const desc = (e.description || "").toLowerCase();
    return LIFECYCLE_KEYWORDS.some(k => desc.includes(k));
  });
  const status = computeLifecycleStatus(lead);
  return (
    <div className="p-4 space-y-4" data-testid="lifecycle-tab-content">
      <div className={`border rounded p-4 ${status.tone === "amber"
        ? "bg-amber-50 border-amber-200"
        : status.tone === "red"
        ? "bg-red-50 border-red-200"
        : status.tone === "neutral"
        ? "bg-neutral-50 border-neutral-200"
        : "bg-emerald-50 border-emerald-200"}`}>
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className={`text-xs font-bold uppercase tracking-wider ${
            status.tone === "amber" ? "text-amber-900" :
            status.tone === "red" ? "text-red-900" :
            status.tone === "neutral" ? "text-neutral-700" :
            "text-emerald-900"
          }`}>{status.label}</span>
          {lead.last_action_date && (
            <span className="text-[11px] text-neutral-500">
              Last action: {lead.last_action_date.slice(0, 10)}
            </span>
          )}
        </div>
        <div className="text-sm mt-2 text-neutral-700">{status.reason}</div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
          Lifecycle events ({lifecycleEvents.length})
        </div>
        {lifecycleEvents.length === 0 ? (
          <div className="text-sm text-neutral-400 italic">
            No lifecycle events recorded for this lead.
          </div>
        ) : (
          <div className="divide-y divide-neutral-100 border border-neutral-200 rounded">
            {lifecycleEvents.map(evt => (
              <div key={evt.event_id} className="px-3 py-2.5 flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#0055FF] mt-1.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-neutral-800 break-words whitespace-pre-wrap leading-snug">
                    {evt.description}
                  </p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-[10px] text-neutral-400 font-mono">
                      {evt.created_at ? formatLocal(evt.created_at) : ""}
                    </span>
                    {evt.user_name && (
                      <span className="text-[10px] text-neutral-400">
                        by {evt.user_name}
                      </span>
                    )}
                    <span className={`text-[10px] px-1.5 py-0.5 font-bold rounded ${evt.event_type === "automated" ? "bg-blue-50 text-blue-700" : "bg-neutral-100 text-neutral-600"}`}>
                      {evt.event_type}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function computeLifecycleStatus(lead) {
  if (lead?.is_quarantined) {
    return {
      tone: "red", label: "Quarantined",
      reason: "Excluded from all marketing flows. " +
              "Investigate before resuming.",
    };
  }
  if (lead?.deletion_requested) {
    return {
      tone: "red", label: "Pending deletion",
      reason: "GDPR / data-removal request in flight.",
    };
  }
  if (lead?.is_paused) {
    return {
      tone: "amber", label: "Paused",
      reason: friendlyPauseReason(lead.pause_reason),
    };
  }
  if (!lead?.is_active) {
    return {
      tone: "neutral", label: "Inactive",
      reason: lead?.lead_status
        ? `Status: ${lead.lead_status}. Not in any drip flow.`
        : "Marked inactive. Not in any drip flow.",
    };
  }
  return {
    tone: "emerald", label: "Active",
    reason: lead?.lead_status
      ? `Status: ${lead.lead_status}. Eligible for drip enrolment.`
      : "Eligible for drip enrolment.",
  };
}

// ─── MARKETING LIFECYCLE ───────────────────────────────────────────────
// What is happening to this person NOW, from the enrolment and send
// tables (GET /crm/leads/:id/marketing-lifecycle) — never inferred from
// Event Log text. A finished drip is history; the panel shows the CURRENT
// workflow (EasyShare L-3517 read "completed" while actively in Long-term
// Nurture). A marketable lead with nothing nurturing it is an error.
const LIFECYCLE_TONE = {
  ok: { box: "bg-emerald-50 border-emerald-200", head: "text-emerald-900" },
  info: { box: "bg-blue-50 border-blue-200", head: "text-blue-900" },
  neutral: { box: "bg-neutral-50 border-neutral-200", head: "text-neutral-700" },
  warning: { box: "bg-amber-50 border-amber-300", head: "text-amber-900" },
  error: { box: "bg-red-50 border-red-300", head: "text-red-800" },
};

function lifecycleDate(iso, withTime = false) {
  if (!iso) return null;
  return withTime ? formatLocal(iso) : formatLocalDate(iso);
}

export function MarketingLifecyclePanel({ leadId, refreshKey }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    api.get(`/crm/leads/${leadId}/marketing-lifecycle`)
      .then((r) => { if (!cancelled) setState({ loading: false, data: r.data, error: null }); })
      .catch((e) => {
        if (cancelled) return;
        const d = e.response?.data?.detail;
        setState({ loading: false, data: null,
          error: (d && typeof d === "object" ? d.message : d) || "Could not load the marketing lifecycle. Refresh to try again." });
      });
    return () => { cancelled = true; };
  }, [leadId, refreshKey]);

  const { loading, data, error } = state;
  if (loading && !data) {
    return <div className="border border-[#E5E5E5] p-4 mb-5 text-xs text-[#A3A3A3]" data-testid="marketing-lifecycle-loading">Loading marketing lifecycle…</div>;
  }
  if (error) {
    return (
      <div className="border border-red-200 bg-red-50 p-4 mb-5 text-sm text-red-800" data-testid="marketing-lifecycle-error">
        <span className="font-bold uppercase text-[10px] tracking-[0.2em] mr-2">Marketing lifecycle</span>{error}
      </div>
    );
  }
  if (!data) return null;
  const tone = LIFECYCLE_TONE[data.tone] || LIFECYCLE_TONE.neutral;
  const cur = data.current;
  const step = cur?.step;
  const last = data.last_marketing_email;
  const rows = [];
  if (cur) {
    rows.push(["Workflow", cur.workflow_name]);
    rows.push(["Enrollment status", cur.enrollment_status]);
    if (step?.label) {
      rows.push(["Current / next step", step.next_email_subject ? `${step.label} — “${step.next_email_subject}”` : step.label]);
    }
    rows.push(["Next scheduled email", cur.next_action_at ? lifecycleDate(cur.next_action_at, true) : "Not scheduled"]);
    if (cur.cadence_label) rows.push(["Cadence", cur.cadence_label]);
  }
  if (data.paused_enrollment) {
    rows.push(["Paused workflow", data.paused_enrollment.workflow_name]);
  }
  rows.push(["Last marketing email", last
    ? `${lifecycleDate(last.sent_at)}${last.subject ? ` — “${last.subject}”` : ""}${last.source ? ` (${last.source})` : ""}`
    : "None sent yet"]);

  const finished = (data.history || []).filter((h) => h.status === "completed");
  return (
    <section className={`border p-4 mb-5 ${tone.box}`} data-testid="marketing-lifecycle-panel" aria-label="Marketing lifecycle">
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#737373]">Marketing lifecycle</span>
        <span className={`text-sm font-black uppercase tracking-wider ${tone.head}`} data-testid="marketing-lifecycle-state" data-state={data.state}>
          {data.label}
        </span>
      </div>
      {data.reason && <p className="text-sm text-[#404040] mt-1" data-testid="marketing-lifecycle-reason">{data.reason}</p>}
      <dl className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-4 gap-y-1 mt-3 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-[10px] uppercase tracking-wider font-bold text-[#737373] pt-0.5">{k}</dt>
            <dd className="text-[#0A0A0A] break-words" data-testid={`marketing-lifecycle-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</dd>
          </div>
        ))}
      </dl>
      {finished.length > 0 && (
        <p className="text-[11px] text-[#737373] mt-3" data-testid="marketing-lifecycle-history">
          History (finished, not current): {finished.map((h) => `${h.workflow_name}${h.completed_at ? ` — completed ${lifecycleDate(h.completed_at)}` : ""}`).join("; ")}
        </p>
      )}
    </section>
  );
}

function friendlyPauseReason(raw) {
  if (!raw) return "No recorded reason — paused before tracking.";
  const map = {
    invalid_email:                "Email bounced — kept paused so we don't keep hitting a bad address.",
    invalid_email_inferred:       "Email bounced (inferred from historical data).",
    unsubscribed:                 "Lead unsubscribed.",
    unsubscribed_inferred:        "Lead unsubscribed (inferred).",
    booking_click:                "Lead clicked a booking CTA — paused awaiting meeting outcome.",
    booking_click_inferred:       "Booked a meeting (inferred).",
    engagement_reply:             "Lead replied to a drip email — paused for human follow-up.",
    do_not_contact_inferred:      "Manually flagged 'do not contact'.",
    orphan_cleanup:               "Stopped during orphan-enrolment cleanup.",
    historical_unknown_inferred:  "Paused before reason tracking existed — no audit trail available.",
  };
  return map[raw] || raw;
}


// "2026-10-29" → "Oct 29, 2026". A follow-up is a calendar date, so it is
// formatted from its parts — never through Date(), which would shift it a
// day for anyone west of UTC.
export function formatFollowupDate(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || "");
  if (!m) return value || "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

// ISO instant → "Oct 29, 2026 08:02 PT" (the business timezone).
export function formatPacific(iso) {
  try {
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles", month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
    return `${p.month} ${p.day}, ${p.year} ${p.hour}:${p.minute} PT`;
  } catch { return iso; }
}

const REMINDER_TONE = {
  sent: "text-emerald-700", scheduled: "text-[#525252]", pending: "text-amber-700",
  completed: "text-emerald-700", not_emailed: "text-[#737373]", problem: "text-red-600",
};

// Follow-up + the reminder email behind it, so an operator can see that
// setting a date really does notify Sales (and when).
export function FollowupStatus({ note, onComplete, busy }) {
  if (!note?.followup_date) return null;
  const r = note.followup_reminder;
  const done = !!note.followup_completed_at;
  let reminder = r?.label || "";
  if (r?.state === "sent" && r.at) reminder = `Reminder: Sent ${formatPacific(r.at)}`;
  else if (r?.state === "scheduled") reminder = `Reminder: Scheduled — ${r.at_label || ""}`.trim();
  else if (r?.state === "pending") reminder = "Reminder: Due — sending shortly";
  else if (r && r.state !== "completed") reminder = `Reminder: ${r.label}`;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px]" data-testid={`note-followup-${note.note_id}`}>
      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 ${done ? "text-emerald-700 bg-emerald-50" : "text-amber-600 bg-amber-50"}`}>
        <Clock size={10} /> Follow-up: {formatFollowupDate(note.followup_date)}
        {done && <span data-testid={`note-followup-done-${note.note_id}`}>&nbsp;— complete{note.followup_completed_by ? ` (${note.followup_completed_by})` : ""}</span>}
      </span>
      {!done && reminder && (
        <span className={REMINDER_TONE[r?.state] || "text-[#737373]"} data-testid={`note-reminder-${note.note_id}`}
              title="Sales gets one reminder email at 08:00 Pacific on the follow-up date; the Morning Digest lists it until it is marked complete.">
          {reminder}
        </span>
      )}
      {!done && onComplete && (
        <button onClick={() => onComplete(note.note_id)} disabled={busy}
                className="text-[#0055FF] hover:text-[#003ECB] font-bold uppercase tracking-wider disabled:opacity-50"
                data-testid={`note-followup-complete-${note.note_id}`}>
          Mark follow-up complete
        </button>
      )}
    </div>
  );
}

function errorText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fallback;
}

function NotesTab({ leadId, notes, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [text, setText] = useState("");
  const [followup, setFollowup] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editFollowup, setEditFollowup] = useState("");
  const addNote = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/crm/leads/${leadId}/notes`, { note_text: text, followup_date: followup || null });
      setText(""); setFollowup(""); setShowAdd(false); onRefresh();
      if (followup) toast.success(`Note saved. Sales will get a reminder email on ${formatFollowupDate(followup)}.`);
    } catch (e) { toast.error(errorText(e, "Note was not saved. Please try again.")); }
    setSaving(false);
  };
  const completeFollowup = async (noteId) => {
    setSaving(true);
    try {
      await api.post(`/crm/leads/${leadId}/notes/${noteId}/followup/complete`);
      toast.success("Follow-up marked complete. The note is kept.");
      onRefresh();
    } catch (e) { toast.error(errorText(e, "Follow-up was not updated. Please try again.")); }
    setSaving(false);
  };
  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try { await api.delete(`/crm/leads/${leadId}/notes/${noteId}`); onRefresh(); } catch (e) { console.error(e); }
  };
  const startEdit = (n) => {
    setEditingId(n.note_id);
    setEditText(n.note_text || "");
    setEditFollowup(n.followup_date || "");
  };
  const cancelEdit = () => { setEditingId(null); setEditText(""); setEditFollowup(""); };
  const saveEdit = async (noteId) => {
    if (!editText.trim()) return toast.error("Note text cannot be empty");
    setSaving(true);
    try {
      // Emptying the date clears the follow-up (and its reminder);
      // null on its own means "unchanged".
      await api.put(`/crm/leads/${leadId}/notes/${noteId}`, {
        note_text: editText,
        followup_date: editFollowup || null,
        clear_followup: !editFollowup,
      });
      cancelEdit();
      onRefresh();
      toast.success("Note updated");
    } catch (e) { toast.error(errorText(e, "Unable to save")); }
    setSaving(false);
  };
  return (
    <div data-testid="notes-tab-content">
      <div className="px-4 py-3 border-b border-[#F5F5F5] flex justify-end">
        <button onClick={() => setShowAdd(p => !p)} className="inline-flex items-center gap-1.5 text-xs text-[#0055FF] hover:text-[#003ECB] font-bold uppercase tracking-wider" data-testid="add-note-btn"><Plus size={12} weight="bold" /> Add Note</button>
      </div>
      {showAdd && (
        <div className="px-4 py-3 bg-[#FAFAFA] border-b border-[#F5F5F5] space-y-2">
          <textarea value={text} onChange={e => setText(e.target.value)} rows={3} placeholder="Note..." className={inputCls} data-testid="note-text-input" />
          <div className="flex items-center gap-2">
            <label className="text-xs text-[#A3A3A3]">Follow-up:</label>
            <input type="date" value={followup} onChange={e => setFollowup(e.target.value)} className="border border-[#E5E5E5] px-2 py-1 text-xs focus:outline-none focus:border-[#0055FF]" />
            <button onClick={addNote} disabled={saving} className="ml-auto px-3 py-1.5 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50" data-testid="save-note-btn">{saving ? "..." : "Save"}</button>
            <button onClick={() => setShowAdd(false)} className="text-xs text-[#A3A3A3]">Cancel</button>
          </div>
        </div>
      )}
      {!notes?.length ? <div className="p-8 text-center text-[#A3A3A3] text-sm">No notes yet</div> : (
        <div className="divide-y divide-[#F5F5F5] max-h-80 overflow-y-auto">
          {notes.map(n => (
            <div key={n.note_id} className="px-4 py-3" data-testid={`note-row-${n.note_id}`}>
              {editingId === n.note_id ? (
                <div className="space-y-2">
                  <textarea
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    rows={3}
                    className={inputCls}
                    data-testid={`note-edit-text-${n.note_id}`}
                  />
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-[#A3A3A3]">Follow-up:</label>
                    <input
                      type="date"
                      value={editFollowup}
                      onChange={e => setEditFollowup(e.target.value)}
                      className="border border-[#E5E5E5] px-2 py-1 text-xs focus:outline-none focus:border-[#0055FF]"
                      data-testid={`note-edit-followup-${n.note_id}`}
                    />
                    <button
                      onClick={() => saveEdit(n.note_id)}
                      disabled={saving}
                      className="ml-auto px-3 py-1.5 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50"
                      data-testid={`note-edit-save-${n.note_id}`}
                    >
                      {saving ? "..." : "Save"}
                    </button>
                    <button onClick={cancelEdit} className="text-xs text-[#A3A3A3]" data-testid={`note-edit-cancel-${n.note_id}`}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-xs text-[#525252] whitespace-pre-wrap">{n.note_text}</p>
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#A3A3A3]">{n.created_at ? formatLocalDate(n.created_at) : ""}</span>
                      {n.added_by_name && <span className="text-[10px] text-[#A3A3A3]">{n.added_by_name}</span>}
                      {n.updated_at && n.updated_at !== n.created_at && (
                        <span className="text-[10px] text-[#A3A3A3] italic">(edited {formatLocalDate(n.updated_at)})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEdit(n)}
                        className="text-[#A3A3A3] hover:text-[#0055FF]"
                        title="Edit note"
                        data-testid={`note-edit-${n.note_id}`}
                      >
                        <PencilSimple size={12} />
                      </button>
                      <button
                        onClick={() => deleteNote(n.note_id)}
                        className="text-red-400 hover:text-red-600"
                        title="Delete note"
                        data-testid={`note-delete-${n.note_id}`}
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                  </div>
                  <FollowupStatus note={n} onComplete={completeFollowup} busy={saving} />
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MeetingsTab({ leadId, meetings, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ meeting_date: "", meeting_time: "", attendees: "", title: "", summary: "", transcript: "" });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const reset = () => setForm({ meeting_date: "", meeting_time: "", attendees: "", title: "", summary: "", transcript: "" });

  const save = async () => {
    if (!form.meeting_date) return toast.error("Meeting date is required");
    if (!form.attendees.trim()) return toast.error("At least one attendee is required");
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/crm/leads/${leadId}/meetings/${editingId}`, form);
        toast.success("Meeting updated");
      } else {
        await api.post(`/crm/leads/${leadId}/meetings`, form);
        toast.success("Meeting logged");
      }
      reset();
      setShowAdd(false);
      setEditingId(null);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const startEdit = (m) => {
    setEditingId(m.meeting_id);
    setShowAdd(true);
    setForm({
      meeting_date: m.meeting_date ? String(m.meeting_date).slice(0, 10) : "",
      meeting_time: m.meeting_time || "",
      attendees: m.attendees || "",
      title: m.title || "",
      summary: m.summary || "",
      transcript: m.transcript || "",
    });
  };

  const cancelEdit = () => { setShowAdd(false); setEditingId(null); reset(); };

  const deleteMeeting = async (id) => {
    if (!window.confirm("Delete this meeting?")) return;
    try { await api.delete(`/crm/leads/${leadId}/meetings/${id}`); onRefresh(); toast.success("Meeting deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="meetings-tab-content">
      <div className="px-4 py-3 border-b border-[#F5F5F5] flex justify-end">
        <button
          onClick={() => { if (showAdd) cancelEdit(); else { reset(); setShowAdd(true); } }}
          className="inline-flex items-center gap-1.5 text-xs text-[#0055FF] hover:text-[#003ECB] font-bold uppercase tracking-wider"
          data-testid="add-meeting-btn"
        >
          <Plus size={12} weight="bold" /> {editingId ? "Editing..." : "Log Meeting"}
        </button>
      </div>
      {showAdd && (
        <div className="px-4 py-3 bg-[#FAFAFA] border-b border-[#F5F5F5] space-y-2" data-testid="meeting-form">
          <div className="flex flex-wrap gap-2">
            <div>
              <label className="text-[10px] text-[#A3A3A3] uppercase tracking-wider block">Date</label>
              <input
                type="date"
                value={form.meeting_date}
                onChange={e => setForm({ ...form, meeting_date: e.target.value })}
                className="border border-[#E5E5E5] px-2 py-1.5 text-xs focus:outline-none focus:border-[#0055FF]"
                data-testid="meeting-date-input"
              />
            </div>
            <div>
              <label className="text-[10px] text-[#A3A3A3] uppercase tracking-wider block">Time</label>
              <input
                type="time"
                value={form.meeting_time}
                onChange={e => setForm({ ...form, meeting_time: e.target.value })}
                className="border border-[#E5E5E5] px-2 py-1.5 text-xs focus:outline-none focus:border-[#0055FF]"
                data-testid="meeting-time-input"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] text-[#A3A3A3] uppercase tracking-wider block">Attendees (comma-separated)</label>
              <input
                type="text"
                value={form.attendees}
                onChange={e => setForm({ ...form, attendees: e.target.value })}
                placeholder="Jane Doe (Acme), Mike P (Eyvo), …"
                className={inputCls}
                data-testid="meeting-attendees-input"
              />
            </div>
          </div>
          <input
            type="text"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="Meeting title (optional)"
            className={inputCls}
            data-testid="meeting-title-input"
          />
          <textarea
            value={form.summary}
            onChange={e => setForm({ ...form, summary: e.target.value })}
            rows={3}
            placeholder="Summary / key takeaways / decisions…"
            className={inputCls}
            data-testid="meeting-summary-input"
          />
          <textarea
            value={form.transcript}
            onChange={e => setForm({ ...form, transcript: e.target.value })}
            rows={5}
            placeholder="Full transcript (optional — paste from Meet / Zoom / Teams)"
            className={inputCls + " font-mono text-[11px]"}
            data-testid="meeting-transcript-input"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="ml-auto px-3 py-1.5 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50"
              data-testid="save-meeting-btn"
            >
              {saving ? "..." : (editingId ? "Update Meeting" : "Save Meeting")}
            </button>
            <button onClick={cancelEdit} className="text-xs text-[#A3A3A3]" data-testid="cancel-meeting-btn">Cancel</button>
          </div>
        </div>
      )}
      {!meetings?.length ? (
        <div className="p-8 text-center text-[#A3A3A3] text-sm">No meetings logged yet</div>
      ) : (
        <div className="divide-y divide-[#F5F5F5] max-h-[500px] overflow-y-auto">
          {meetings.map(m => {
            const expanded = expandedId === m.meeting_id;
            return (
              <div key={m.meeting_id} className="px-4 py-3" data-testid={`meeting-row-${m.meeting_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#0A0A0A] bg-[#EFF6FF] text-[#1D4ED8] px-2 py-0.5">
                        <Clock size={12} /> {m.meeting_date ? String(m.meeting_date).slice(0, 10) : ""}
                        {m.meeting_time ? ` · ${m.meeting_time}` : ""}
                      </span>
                      {m.title && <span className="text-xs font-bold text-[#0A0A0A]">{m.title}</span>}
                    </div>
                    <p className="text-[11px] text-[#525252] mt-1">
                      <span className="text-[#A3A3A3] uppercase tracking-wider text-[9px] font-bold mr-1">Attendees:</span>
                      {m.attendees}
                    </p>
                    {m.summary && !expanded && (
                      <p className="text-xs text-[#525252] mt-1.5 line-clamp-2 whitespace-pre-wrap">{m.summary}</p>
                    )}
                    {expanded && (
                      <div className="mt-2 space-y-2">
                        {m.summary && (
                          <div>
                            <p className="text-[9px] font-bold text-[#A3A3A3] uppercase tracking-wider">Summary</p>
                            <p className="text-xs text-[#0A0A0A] whitespace-pre-wrap mt-0.5">{m.summary}</p>
                          </div>
                        )}
                        {m.transcript && (
                          <div>
                            <p className="text-[9px] font-bold text-[#A3A3A3] uppercase tracking-wider">Transcript</p>
                            <pre className="text-[11px] text-[#0A0A0A] whitespace-pre-wrap font-mono bg-[#FAFAFA] border border-[#F5F5F5] p-2 mt-0.5 max-h-72 overflow-y-auto">{m.transcript}</pre>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] text-[#A3A3A3]">Logged {m.created_at ? formatLocalDate(m.created_at) : ""}</span>
                      {m.added_by_name && <span className="text-[10px] text-[#A3A3A3]">by {m.added_by_name}</span>}
                      {m.updated_at && m.created_at && m.updated_at !== m.created_at && (
                        <span className="text-[10px] text-[#A3A3A3] italic">(edited)</span>
                      )}
                      {(m.summary || m.transcript) && (
                        <button
                          onClick={() => setExpandedId(expanded ? null : m.meeting_id)}
                          className="text-[10px] text-[#0055FF] hover:underline"
                          data-testid={`meeting-toggle-${m.meeting_id}`}
                        >
                          {expanded ? "Collapse" : "Expand"}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => startEdit(m)}
                      className="text-[#A3A3A3] hover:text-[#0055FF]"
                      title="Edit meeting"
                      data-testid={`meeting-edit-${m.meeting_id}`}
                    >
                      <PencilSimple size={12} />
                    </button>
                    <button
                      onClick={() => deleteMeeting(m.meeting_id)}
                      className="text-red-400 hover:text-red-600"
                      title="Delete meeting"
                      data-testid={`meeting-delete-${m.meeting_id}`}
                    >
                      <Trash size={12} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EmailLogTab({ leadId, emails, onRefresh }) {
  const { user } = useAuth();
  const tz = resolveTz(user);
  const [showCompose, setShowCompose] = useState(false);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [tracked, setTracked] = useState([]);
  const [openMsg, setOpenMsg] = useState(null);
  const [openMsgLoading, setOpenMsgLoading] = useState(false);

  useEffect(() => {
    if (leadId) {
      api.get(`/crm/leads/${leadId}/emails`).then(r => setTracked(r.data || [])).catch(() => setTracked([]));
    }
  }, [leadId, emails]);

  const sendEmail = async () => {
    if (!subject.trim() || !bodyHtml.trim()) return;
    setSending(true);
    try { await api.post(`/crm/leads/${leadId}/send-email`, { subject, body_html: bodyHtml }); setSubject(""); setBodyHtml(""); setShowCompose(false); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Email was not sent. Please try again."); }
    setSending(false);
  };

  const viewMsg = async (msgId) => {
    setOpenMsgLoading(true);
    try {
      const res = await api.get(`/crm/leads/${leadId}/emails/${encodeURIComponent(msgId)}`);
      setOpenMsg(res.data);
    } catch (e) { alert("Failed to load email body"); }
    setOpenMsgLoading(false);
  };

  // Merge: newest first, prefer tracked (new email_messages) then campaign log items
  const merged = [
    ...tracked.map(t => ({
      key: "msg:" + t.message_id,
      when: t.sent_at,
      subject: t.subject || "(no subject)",
      recipient: t.to_email || "",
      status: t.status || t.direction,
      direction: t.direction,
      message_id: t.message_id,
      // Marketing workflow / nurture sends (from mkt_sends) carry their workflow.
      campaignName: t.workflow_name
        ? (t.related_entity_type === "nurture" ? `${t.workflow_name} · nurture library` : t.workflow_name)
        : undefined,
      badgeClass: t.status === "failed"
        ? "bg-red-50 text-red-700"
        : t.direction === "inbound" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700",
    })),
    ...(emails || []).map(e => ({
      key: "log:" + e.email_log_id,
      when: e.email_date,
      subject: e.email_subject,
      recipient: e.to_email || "",
      status: e.email_status,
      campaignName: e.campaign_name,
      badgeClass: EMAIL_STATUS[e.email_status] || "bg-[#F5F5F5] text-[#525252]",
    })),
  ].sort((a, b) => (b.when || "").localeCompare(a.when || ""));

  return (
    <div data-testid="email-log-tab-content">
      <div className="px-4 py-3 border-b border-[#F5F5F5] flex justify-end">
        <button onClick={() => setShowCompose(p => !p)} className="inline-flex items-center gap-1.5 text-xs text-[#0055FF] hover:text-[#003ECB] font-bold uppercase tracking-wider" data-testid="compose-email-btn"><Envelope size={12} weight="bold" /> Send Email</button>
      </div>
      {showCompose && (
        <div className="px-4 py-3 bg-[#FAFAFA] border-b border-[#F5F5F5] space-y-3">
          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" className={inputCls} data-testid="email-subject-input" />
          <div><label className="text-xs font-bold text-[#525252] uppercase tracking-wider mb-1 block">Body</label><HtmlEditor value={bodyHtml} onChange={setBodyHtml} placeholder="Compose your email..." minHeight={180} testId="email-body-editor" /></div>
          <div className="flex gap-2">
            <button onClick={sendEmail} disabled={sending || !subject.trim() || !bodyHtml.trim()} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50" data-testid="send-email-btn"><PaperPlaneTilt size={11} weight="bold" /> {sending ? "Sending..." : "Send"}</button>
            <button onClick={() => setShowCompose(false)} className="text-xs text-[#A3A3A3]">Cancel</button>
          </div>
        </div>
      )}
      {!merged.length ? <div className="p-8 text-center text-[#A3A3A3] text-sm">No emails logged yet</div> : (
        <div className="divide-y divide-[#F5F5F5] max-h-[420px] overflow-y-auto" data-testid="tracked-emails-list">
          {merged.map(m => {
            const clickable = !!m.message_id;
            return (
              <div
                key={m.key}
                onClick={clickable ? () => viewMsg(m.message_id) : undefined}
                className={"px-4 py-3 flex items-start justify-between gap-2 " + (clickable ? "cursor-pointer hover:bg-[#F9FAFB]" : "")}
                data-testid={clickable ? `tracked-email-row-${m.message_id}` : undefined}
                title={clickable ? "Click to view email content" : ""}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-[#0A0A0A] truncate">{m.subject}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-[10px] text-[#A3A3A3] font-mono">{m.when ? formatTz(m.when, tz) : ""}</span>
                    {m.recipient && <span className="text-[10px] text-[#525252] truncate">· {m.direction === "inbound" ? "from" : "to"} {m.recipient}</span>}
                    {m.campaignName && <span className="text-[10px] text-[#A3A3A3]">· {m.campaignName}</span>}
                  </div>
                </div>
                <span className={`px-2 py-0.5 text-[10px] font-bold flex-shrink-0 ${m.badgeClass}`}>{m.status}</span>
              </div>
            );
          })}
        </div>
      )}
      <Dialog open={!!openMsg || openMsgLoading} onOpenChange={(o) => { if (!o) setOpenMsg(null); }}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-3xl max-h-[90vh] p-0 overflow-hidden" data-testid="email-body-dialog">
          <DialogHeader className="p-4 border-b border-[#E5E5E5]">
            <DialogTitle className="font-bold tracking-tight text-sm" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {openMsgLoading ? "Loading…" : (openMsg?.subject || "Email")}
            </DialogTitle>
            {openMsg && (
              <div className="text-[11px] text-[#525252] mt-1 space-y-0.5 font-mono">
                <div><span className="text-[#A3A3A3] uppercase tracking-wider">From:</span> {openMsg.from_name ? `${openMsg.from_name} <${openMsg.from_email}>` : openMsg.from_email}</div>
                <div><span className="text-[#A3A3A3] uppercase tracking-wider">To:</span> {openMsg.to_email}</div>
                {openMsg.reply_to_email && <div><span className="text-[#A3A3A3] uppercase tracking-wider">Reply-To:</span> {openMsg.reply_to_email}</div>}
                <div><span className="text-[#A3A3A3] uppercase tracking-wider">Sent:</span> {openMsg.sent_at ? formatTz(openMsg.sent_at, tz) : ""}</div>
                {openMsg.attachments_count > 0 && <div><span className="text-[#A3A3A3] uppercase tracking-wider">Attachments:</span> {openMsg.attachments_count}</div>}
              </div>
            )}
          </DialogHeader>
          <div className="p-0 overflow-auto bg-white" style={{ maxHeight: "70vh" }}>
            {openMsg && (
              // Session 46 — inbound email body is rendered in a
              // sandbox so if a bad actor ever landed HTML with
              // <script>/<iframe>/<form> inside, it cannot touch
              // the parent DOM, cookies, localStorage, or make
              // network calls. ``allow-same-origin`` is NOT granted,
              // which is what makes this sandbox actually safe.
              <iframe
                /* XSS: inbound email HTML rendered in a null-origin sandbox.
                   srcDoc is further sanitised with DOMPurify so even if a
                   sandbox policy bug is ever discovered, there is nothing
                   dangerous to execute. */
                srcDoc={safeSrcDoc(openMsg.body_html) || ""}
                sandbox=""
                referrerPolicy="no-referrer"
                className="w-full"
                style={{ minHeight: "60vh", border: 0 }}
                title="Email body"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// NOTE: Defined at module scope (NOT inside LeadDetail) so React keeps the
// same component identity across renders. Defining these inside LeadDetail
// re-creates the component class on every keystroke, unmounting/remounting
// the underlying <input> — which loses focus, closes the native date picker,
// and (in some browsers) drops the typed value before onChange fires. This
// was the root cause of "demo booked / discovery date not saving".
const Field = ({ label, value, field, type = "text", options, editing: ed, form: fm, set: setFn }) => (
  <div className="flex">
    <div className={cellLabel}>{label}</div>
    <div className={cellValue + " flex-1"}>
      {ed && field ? (
        options ? (
          <select value={fm[field] || ""} onChange={e => setFn(field, e.target.value)} className={inputCls}>
            {options.map(o => <option key={o} value={o}>{o || "(none)"}</option>)}
          </select>
        ) : (
          <input type={type} value={fm[field] ?? ""} onChange={e => setFn(field, e.target.value)} className={inputCls} />
        )
      ) : (
        <span className={value ? "" : "text-[#A3A3A3]"}>{value || "-"}</span>
      )}
    </div>
  </div>
);

// "BCC to Pulse" row — the lead's unique archive address (built by the
// backend, `lead.bcc_address`) with one-click copy. See BccAddressCopy.
const BccEmailField = ({ label, address }) => (
  <div className="flex">
    <div className={cellLabel} title="BCC this address when you email this contact from Gmail or Outlook, and the message is archived in Pulse under Externally Sent Emails.">{label}</div>
    <div className={cellValue + " flex-1 flex items-center gap-2 min-w-0"}>
      <BccAddressCopy address={address} />
    </div>
  </div>
);

// Compact stacked-label cell used by the date-timeline strip. Stable
// component identity (module scope) is critical so typing into a date input
// does not unmount it mid-edit.
const DateCell = ({ label, value, field, editing: ed, form: fm, set: setFn }) => (
  <div className="bg-[#FAFAFA] border border-[#E5E5E5] px-3 py-2" data-testid={`date-cell-${field}`}>
    <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#A3A3A3] mb-1">{label}</div>
    {ed ? (
      <input
        type="date"
        value={(fm[field] || "").slice(0, 10)}
        onChange={e => setFn(field, e.target.value)}
        className="w-full h-7 px-1.5 text-xs border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF] bg-white"
        data-testid={`date-input-${field}`}
      />
    ) : (
      <div className={`text-sm font-mono ${value ? "text-[#0A0A0A]" : "text-[#A3A3A3]"}`}>{value ? formatLocalDate(value) : "—"}</div>
    )}
  </div>
);


// ──────────────────────────────────────────────────────────────────────────
// Marketing Sends tab — campaigns + workflow drips this lead has received
// with per-send open / click counts (bot-filtered).
// ──────────────────────────────────────────────────────────────────────────
function MarketingSendsTab({ leadId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/crm/leads/${leadId}/marketing-emails`)
      .then(r => setData(r.data))
      .catch(() => setData({ items: [] }))
      .finally(() => setLoading(false));
  }, [leadId]);

  if (loading) return <div className="p-5 text-sm text-[#A3A3A3]">Loading marketing sends…</div>;
  const items = data?.items || [];
  if (items.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-[#525252]">No marketing emails sent to this lead yet.</p>
        <p className="text-xs text-[#A3A3A3] mt-1">Campaigns and workflow drips that this lead receives will show up here with open and click counts.</p>
      </div>
    );
  }
  return (
    <div className="divide-y divide-[#F5F5F5] max-h-[480px] overflow-y-auto" data-testid="marketing-sends-list">
      {items.map((s) => (
        <div key={s.send_id} className="p-3 hover:bg-[#FAFAFA]" data-testid={`marketing-send-${s.send_id}`}>
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[#0A0A0A] text-sm">
                  {s.campaign_name || s.workflow_name || "Untitled send"}
                </span>
                {s.workflow_name && s.workflow_step_index != null && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-[#EFF6FF] text-[#0055FF] font-bold uppercase tracking-wider border border-[#BFDBFE]">
                    Step {s.workflow_step_index + 1}
                  </span>
                )}
                <SendStatusChip status={s.status} />
              </div>
              {s.subject && <p className="text-xs text-[#525252] truncate mt-0.5" title={s.subject}>{s.subject}</p>}
              <div className="flex items-center gap-3 text-[11px] text-[#A3A3A3] mt-1 tabular-nums">
                <span>Sent {s.sent_at || s.queued_at ? new Date(s.sent_at || s.queued_at).toLocaleString() : "—"}</span>
                {(s.opens > 0 || s.clicks > 0) && (
                  <span>· Last activity {s.last_event_at ? new Date(s.last_event_at).toLocaleString() : "—"}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Stat label="Opens" value={s.opens} active={s.opens > 0} color="#16A34A" />
              <Stat label="Clicks" value={s.clicks} active={s.clicks > 0} color="#0055FF" />
              {s.unsubscribed && (
                <span className="text-[10px] px-1.5 py-0.5 bg-[#FEF2F2] text-[#DC2626] font-bold uppercase tracking-wider border border-[#FECACA]">Unsub</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SendStatusChip({ status }) {
  const map = {
    sent:        { bg: "#F0FDF4", fg: "#16A34A", border: "#BBF7D0" },
    delivered:   { bg: "#F0FDF4", fg: "#16A34A", border: "#BBF7D0" },
    bounced:     { bg: "#FEF2F2", fg: "#DC2626", border: "#FECACA" },
    dropped:     { bg: "#FEF2F2", fg: "#DC2626", border: "#FECACA" },
    blocked:     { bg: "#FEF2F2", fg: "#DC2626", border: "#FECACA" },
    spam:        { bg: "#FEF2F2", fg: "#DC2626", border: "#FECACA" },
    queued:      { bg: "#F5F5F5", fg: "#525252", border: "#E5E5E5" },
    sending:     { bg: "#EFF6FF", fg: "#0055FF", border: "#BFDBFE" },
    failed:      { bg: "#FEF2F2", fg: "#DC2626", border: "#FECACA" },
  };
  const cfg = map[status] || { bg: "#F5F5F5", fg: "#525252", border: "#E5E5E5" };
  return (
    <span className="text-[10px] px-1.5 py-0.5 font-bold uppercase tracking-wider border"
          style={{ backgroundColor: cfg.bg, color: cfg.fg, borderColor: cfg.border }}>
      {status}
    </span>
  );
}

function Stat({ label, value, active, color }) {
  return (
    <div className="text-right">
      <div className={`text-base font-black tabular-nums leading-none ${active ? "" : "text-[#A3A3A3]"}`}
           style={{ color: active ? color : undefined, fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        {value || 0}
      </div>
      <div className="text-[9px] text-[#A3A3A3] uppercase tracking-wider font-bold mt-0.5">{label}</div>
    </div>
  );
}


export default function LeadDetail() {
  const { leadId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // { status, message } | null
  const [activeTab, setActiveTab] = useState("notes");
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [addressLookup, setAddressLookup] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [lookingUp, setLookingUp] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await api.get(`/crm/leads/${leadId}`);
      if (res.data?.merged && res.data.merged_into_lead_id) {
        // One lead per email address — a merged duplicate's old link lands
        // on the surviving lead.
        toast.info(`${res.data.lead_ref || "This lead"} was merged into ${res.data.merged_into_lead_ref || "another lead"} (one lead per email address).`);
        navigate(`/crm/leads/${res.data.merged_into_lead_id}`, { replace: true });
        return;
      }
      setData(res.data);
      setForm(res.data.lead);
    } catch (e) {
      console.error(e);
      const status = e.response?.status;
      setLoadError({
        status,
        message: status === 404
          ? "This lead no longer exists. It may have been deleted."
          : status === 403
            ? "You don't have permission to view this lead."
            : "Could not load this lead. Check your connection and try again.",
      });
    }
    setLoading(false);
  }, [leadId, navigate]);

  useEffect(() => { load(); }, [load]);

  const set = useCallback((k, v) => setForm(p => ({ ...p, [k]: v })), []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/crm/leads/${leadId}`, form);
      setData(d => ({ ...d, lead: res.data }));
      setForm(res.data);
      setEditing(false);
      toast.success("Lead updated");
    } catch (e) {
      const d = e.response?.data?.detail;
      // e.g. LEAD_EMAIL_EXISTS — another lead already owns the new address.
      toast.error((d && typeof d === "object" ? d.message : d) || "Lead was not saved. Please try again.");
    }
    setSaving(false);
  };

  const lookupAddress = async (query) => {
    const q = query || addressLookup;
    if (!q.trim()) { setAddressSuggestions([]); return; }
    setLookingUp(true);
    try {
      const country = (form.country || "").toLowerCase();
      const countryCode = /^(united kingdom|uk|gb|england|scotland|wales)/i.test(country) ? "gb" : "us";
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&addressdetails=1&limit=5&countrycodes=${countryCode}`);
      const results = await res.json();
      setAddressSuggestions(results);
    } catch { }
    setLookingUp(false);
  };

  // Debounced typeahead
  const lookupTimeout = useRef(null);
  const onAddressInput = (val) => {
    setAddressLookup(val);
    if (lookupTimeout.current) clearTimeout(lookupTimeout.current);
    if (val.length >= 3) {
      lookupTimeout.current = setTimeout(() => lookupAddress(val), 350);
    } else {
      setAddressSuggestions([]);
    }
  };

  const applyAddress = (result) => {
    const addr = result.address || {};
    setForm(p => ({
      ...p,
      address_line_1: [addr.house_number, addr.road].filter(Boolean).join(" ") || result.display_name?.split(",")[0] || "",
      address_line_2: addr.suburb || addr.neighbourhood || "",
      city: addr.city || addr.town || addr.village || addr.municipality || "",
      state_region: addr.state || addr.county || "",
      postal_code: addr.postcode || "",
      country: addr.country || "",
    }));
    setAddressSuggestions([]);
    setAddressLookup("");
    toast.success("Address applied");
  };

  const isAdmin = user?.role === "platform_admin";

  const requestDelete = async () => {
    // Two paths depending on role. Admins get a real, irreversible delete
    // with a stronger confirm so they can't trigger it by accident; everyone
    // else can only flag the lead for an admin to finalise.
    if (isAdmin) {
      const company = lead.company_name || "this lead";
      const confirm1 = window.confirm(
        `PERMANENTLY DELETE "${company}"?\n\n` +
        `This cannot be undone. The lead, all its events, notes, meetings, ` +
        `email logs and campaign assignments will be removed from the database.\n\n` +
        `Click OK to continue, then confirm one more time.`
      );
      if (!confirm1) return;
      const typed = window.prompt(
        `Type DELETE in caps to confirm hard delete of "${company}":`
      );
      if (typed !== "DELETE") {
        toast.error("Hard delete cancelled — confirmation did not match.");
        return;
      }
    } else if (lead.deletion_requested) {
      toast.info("Deletion already requested — a platform admin must finalise.");
      return;
    } else {
      if (!window.confirm("Submit a deletion request? A platform admin must approve before the lead is removed.")) return;
    }
    setDeleting(true);
    try {
      const r = await api.delete(`/crm/leads/${leadId}`);
      if (r.data?.status === "deleted") {
        toast.success("Lead permanently deleted.");
        navigate("/crm/leads");
        return;
      }
      toast.success(r.data?.message || "Deletion request submitted.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
    setDeleting(false);
  };

  const togglePause = async () => {
    if (lead.is_paused) {
      if (!window.confirm("Resume this lead?\n\nFuture campaign emails and workflow enrolments will be allowed again.")) return;
      try {
        await api.post(`/crm/leads/${leadId}/resume`);
        toast.success("Lead resumed — emails will resume normally.");
        load();
      } catch (e) { toast.error(e.response?.data?.detail || "Resume failed"); }
      return;
    }
    const reason = window.prompt(
      "PAUSE this lead — no chase emails will go out until resumed.\n\n" +
      "Optionally, why are we pausing? (do-not-contact, competitor, on hold…)",
      "Do not contact — flagged by sales"
    );
    if (reason === null) return;
    try {
      await api.post(`/crm/leads/${leadId}/pause`, { reason: reason || "Manual pause" });
      toast.success("Lead paused — all outbound emails suppressed.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Pause failed"); }
  };

  // Session 90 — Re-validate this lead's email domain via three
  // independent DNS resolvers. Used to recover leads incorrectly
  // flagged `invalid_email` by an automated sweep (e.g. the
  // 2026-04-28 false-positive batch that mis-flagged gmail.com,
  // drexel.edu, va.gov, etc. due to a resolver glitch).
  const [revalidating, setRevalidating] = useState(false);
  const revalidateEmail = async () => {
    if (revalidating) return;
    setRevalidating(true);
    try {
      const res = await api.post(`/crm/leads/${leadId}/revalidate-email`);
      const d = res.data || {};
      if (d.reactivated) {
        toast.success(
          `${d.email_domain} confirmed deliverable — lead reactivated.`,
        );
      } else if (d.verdict === "DELIVERABLE") {
        toast.success(
          `${d.email_domain} is deliverable. Nothing to clear — lead wasn't flagged invalid.`,
        );
      } else if (d.verdict === "DEAD") {
        toast.error(
          `${d.email_domain} is NXDOMAIN across all resolvers — flag retained.`,
        );
      } else {
        toast.info(
          `${d.email_domain} returned ambiguous DNS. Try again in a few minutes.`,
        );
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-validation failed");
    } finally {
      setRevalidating(false);
    }
  };

  const promoteToClient = async () => {
    if (!window.confirm("Promote this lead to a full client?\n\nThis will:\n• Create a new Client record with all lead data\n• Re-parent any Lead projects to the new client\n• Mark the lead as Closed Won (inactive)\n\nContinue?")) return;
    try {
      const res = await api.post(`/crm/leads/${leadId}/promote`);
      toast.success(`Promoted to client "${res.data.company_name}". ${res.data.projects_reparented} project(s) moved.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Promote failed");
    }
  };

  const markReplied = async () => {
    if (!window.confirm(
      "Mark this lead as Replied?\n\n" +
      "This will:\n" +
      "• Pause active workflow enrolments\n" +
      "• Log a 'replied' event on the timeline\n" +
      "• Mark the lead HOT (a reply is the only automatic HOT signal)\n\n" +
      "Use only when the lead has actually replied to us — not for opens, clicks or emails we sent."
    )) return;
    try {
      const res = await api.post(`/crm/leads/${leadId}/mark-replied`, {});
      toast.success(
        `Paused ${res.data.workflows_paused} workflow(s)` +
        (res.data.promoted_to_hot ? " · lead marked HOT" : "")
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mark replied failed");
    }
  };

  const toggleBooked = async () => {
    const isBooked = !!(lead && lead.demo_booked_date);
    const verb = isBooked ? "unmark" : "mark";
    if (!window.confirm(
      `Are you sure you want to ${verb} this lead as booked?\n\n` +
      (isBooked
        ? "This will clear the Demo Booked date — engagement follow-ups can resume firing."
        : "This will set the Demo Booked date to today AND suppress any future automatic engagement follow-ups for this lead.")
    )) return;
    try {
      await api.post(`/marketing/leads/${leadId}/booking-flag`,
        { booked: !isBooked });
      toast.success(isBooked ? "Booked flag cleared" : "Marked as booked");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  // One-click "Lost" action. Confirms first, accepts an optional reason
  // via a single prompt (matches the operator preference for a quick
  // workflow — they don't want to fill out a form every time, but they
  // CAN add context if they want to). The endpoint cascades a pause so
  // no workflows keep firing at a lost prospect.
  const markLost = async () => {
    if (!window.confirm(
      `Mark "${lead.company_name}" as Lost?\n\n` +
      "This will:\n" +
      "• Set lead category to 'Closed Lost'\n" +
      "• Pause active workflow enrolments\n" +
      "• Log the event on the timeline\n\n" +
      "Optional reason on the next prompt (you can leave it blank)."
    )) return;
    const reason = (window.prompt("Optional reason for marking Lost (leave blank to skip):") || "").trim();
    try {
      await api.post(`/crm/leads/${leadId}/mark-lost`, reason ? { reason } : {});
      toast.success("Lead marked Lost");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mark Lost failed");
    }
  };

  // ── Manual reminder (Session 1.15.2 / 1.15.3) ───────────────────────
  // One-off "just checking in" email outside of any drip campaign.
  // Stamps last_action_date and resumes any paused workflow enrolments
  // so the drip picks back up at the next 7-day slot. Body uses the
  // shared TipTap WYSIWYG editor (same component as the campaign
  // editor) so operators get tokens, brand assets, image upload, etc.
  // The modal also offers an Edit/Preview toggle and a "Send Test to
  // Me" button that routes the same body to the operator's own inbox
  // for a final visual check before pulling the trigger.
  // Pacific-time helpers — Session 67 schedule-send pinned to PT so reps
  // anywhere in the world get a consistent baseline. PT honours DST
  // automatically (PST = UTC-8 Nov–Mar, PDT = UTC-7 Mar–Nov) via the
  // browser's IANA zone database, so we don't ship our own DST table.
  const getPtOffsetMin = (dt) => {
    // Returns the PT offset in minutes (e.g. -480 for PST, -420 for PDT)
    const part = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      timeZoneName: "longOffset",
      hour: "numeric",
    }).formatToParts(dt).find(p => p.type === "timeZoneName");
    const m = (part?.value || "").match(/GMT([+-])(\d{2}):(\d{2})/);
    if (!m) return -480; // fallback PST
    const sign = m[1] === "+" ? 1 : -1;
    return sign * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));
  };
  const getPtAbbrev = (dt) => (
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      timeZoneName: "short",
    }).formatToParts(dt).find(p => p.type === "timeZoneName")?.value || "PT"
  );
  // Convert a "YYYY-MM-DDTHH:mm" string from <input type="datetime-local">
  // — interpreted as PT wall-clock — into the canonical ISO-UTC string
  // the backend expects.
  const pacificLocalToUtcIso = (localStr) => {
    if (!localStr) return null;
    // Step 1: parse the string AS IF it were UTC. This gives us the
    // wrong instant, but the wall-clock components match the picker.
    const asUtc = new Date(localStr + ":00Z");
    if (isNaN(asUtc.getTime())) return null;
    // Step 2: subtract PT's offset so the wall-clock reads the same in PT.
    const offsetMin = getPtOffsetMin(asUtc);
    return new Date(asUtc.getTime() - offsetMin * 60_000).toISOString();
  };

  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderSubject, setReminderSubject] = useState("");
  const [reminderBody, setReminderBody] = useState("");
  const [reminderMode, setReminderMode] = useState("edit"); // "edit" | "preview"
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderTesting, setReminderTesting] = useState(false);
  // Schedule-send state — Session 67. When `reminderScheduleAt` is a
  // non-empty ISO datetime string, the Schedule button is active and
  // posts `scheduled_at` to the backend so the dispatcher fires the
  // reminder later. Empty string = "send now" path.
  const [reminderScheduleAt, setReminderScheduleAt] = useState("");
  const [reminderScheduling, setReminderScheduling] = useState(false);
  const [scheduledReminders, setScheduledReminders] = useState([]);
  // Session 73 — booking-check prompts (set when a lead clicks the meeting
  // link). Render a Yes/No banner under the title bar when at least one is
  // pending. Refreshes from /api/crm/booking-checks/lead/{id}.
  const [bookingChecks, setBookingChecks] = useState([]);
  const [bookingDecisionInFlight, setBookingDecisionInFlight] = useState(false);

  const refreshBookingChecks = useCallback(async () => {
    try {
      const r = await api.get(`/crm/booking-checks/lead/${leadId}`);
      setBookingChecks(r.data?.items || []);
    } catch {
      /* non-fatal */
    }
  }, [leadId]);

  useEffect(() => {
    if (leadId) refreshBookingChecks();
  }, [leadId, refreshBookingChecks]);

  const decideBookingCheck = async (checkId, decision, defaultNotes = "") => {
    if (bookingDecisionInFlight) return;
    const notes = window.prompt(
      decision === "booked"
        ? "Add a note (optional) — e.g. when the meeting is scheduled."
        : "Add a note (optional) — e.g. 'asked them, no booking yet'.",
      defaultNotes
    );
    if (notes === null) return; // user cancelled
    setBookingDecisionInFlight(true);
    try {
      await api.post(
        `/crm/booking-checks/${checkId}/${decision === "booked" ? "booked" : "not-booked"}`,
        { notes }
      );
      if (decision === "booked") {
        toast.success("Marked as booked — drip stays paused, demo date set.");
      } else {
        toast.success("No booking — drip resumed from where they paused.");
      }
      await Promise.all([refreshBookingChecks(), load()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decision failed");
    }
    setBookingDecisionInFlight(false);
  };

  // Pre-fill subject + body with concrete personalised text the moment
  // the modal opens — operators can then tweak in the editor. We seed
  // with real values (not tokens like {{first_name}}) so the WYSIWYG
  // shows what the lead will actually see.
  //
  // Brand styling: Verdana + the darkest navy from our TipTap palette
  // (`#0D47A1` — Material Blue 900). Wrap each paragraph's text in a
  // `<span>` (which TipTap's TextStyle/FontFamily/Color marks emit)
  // so both the editor preview AND the sent email render correctly.
  // The outer <div style="…"> is a belt-and-braces fallback for email
  // clients that strip per-paragraph styles (Outlook desktop).
  //
  // The leading `{{SNIPPET:cblk_8aeb3d3d-a8f}}` token is the Eyvo
  // Procurement nav-header content block — `email_html_postprocess`
  // expands it server-side at send time so the rendered email shows the
  // branded header. Keep the token literal in the editor (don't expand
  // here) so operators can tweak the header without touching the body.
  const seedReminderDefaults = () => {
    if (!lead) return;
    // Prefer the explicit first-name field (Session 67). Falls back to
    // splitting `contact_person` for legacy leads that haven't been
    // auto-migrated yet.
    const firstName = (
      (lead.contact_first_name || "").trim()
      || (lead.contact_person || "").split(" ")[0]
      || "there"
    );
    const company = lead.company_name || "your team";
    setReminderSubject(`Following up on our recent conversation — Eyvo/${company}`);
    const FONT = "font-family: Verdana, Geneva, sans-serif; color: #0D47A1;";
    const span = (txt) => `<span style="${FONT}">${txt}</span>`;
    // The word "link" is hyperlinked to {{CalendarLink}} — the backend
    // expands that token to the env-configured Zoom scheduler URL just
    // before SendGrid handoff so we never hardcode the URL into source.
    const linkAnchor = `<a href="{{CalendarLink}}" style="${FONT}text-decoration:underline;">link</a>`;
    setReminderBody(
      `<p>{{SNIPPET:cblk_8aeb3d3d-a8f}}</p>` +
      `<div style="${FONT}">` +
        `<p>${span(`Hi ${firstName},`)}</p>` +
        `<p>${span(
          "Following our recent conversation about Eyvo, I wanted to circle back to see if you have any further questions or if there's anything we can help with as you evaluate next steps and also to determine the timeline you might be working with."
        )}</p>` +
        `<p>` + span(
          "Happy to set up another call whenever works best — just reply with a few times that work for you, or feel free to use our "
        ) + linkAnchor + span(".") + `</p>` +
        `<p>${span("Thanks &amp; Regards")}</p>` +
        `<p><br><br><br></p>` +
        `<p>${span("<strong>Michael Petter</strong><br>Director<br>Eyvo, Inc.")}</p>` +
      `</div>`
    );
    setReminderMode("edit");
    setReminderScheduleAt("");
  };

  const openReminderModal = async () => {
    seedReminderDefaults();
    setShowReminderModal(true);
    // Fetch existing scheduled reminders so we can show "X reminder(s)
    // already pending — view & cancel" without an extra click.
    try {
      const r = await api.get(`/crm/leads/${leadId}/scheduled-reminders`);
      setScheduledReminders(r.data || []);
    } catch { /* non-fatal */ }
  };

  const sendManualReminder = async () => {
    if (reminderSending) return;
    setReminderSending(true);
    try {
      const r = await api.post(`/crm/leads/${leadId}/send-manual-reminder`, {
        subject: reminderSubject.trim() || undefined,
        body_html: reminderBody.trim() || undefined,
      });
      const resumed = r.data.resumed_workflows
        ? ` (also resumed ${r.data.resumed_workflows} paused workflow${r.data.resumed_workflows === 1 ? "" : "s"})`
        : "";
      toast.success(`Reminder sent to ${r.data.to}${resumed}`);
      setShowReminderModal(false);
      setReminderSubject("");
      setReminderBody("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reminder send failed");
    }
    setReminderSending(false);
  };

  const scheduleManualReminder = async () => {
    if (reminderScheduling) return;
    if (!reminderScheduleAt) {
      toast.error("Pick a date & time first");
      return;
    }
    // The picker value is "YYYY-MM-DDTHH:mm" — we interpret it as
    // Pacific Time (regardless of where the operator's browser is) so
    // a 9:00 AM scheduled send means 9:00 AM in Eyvo HQ's timezone.
    const isoUtc = pacificLocalToUtcIso(reminderScheduleAt);
    if (!isoUtc) {
      toast.error("Invalid scheduled date/time");
      return;
    }
    setReminderScheduling(true);
    try {
      const r = await api.post(`/crm/leads/${leadId}/send-manual-reminder`, {
        subject: reminderSubject.trim() || undefined,
        body_html: reminderBody.trim() || undefined,
        scheduled_at: isoUtc,
      });
      // Echo back the scheduled time in PT so the operator's confirmation
      // matches what they picked (instead of getting their browser's
      // localised conversion).
      const tz = getPtAbbrev(new Date(isoUtc));
      const ptEcho = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles",
        dateStyle: "medium", timeStyle: "short",
      }).format(new Date(isoUtc));
      toast.success(
        `Reminder scheduled for ${ptEcho} ${tz} — will send to ${r.data.to}`
      );
      setShowReminderModal(false);
      setReminderSubject("");
      setReminderBody("");
      setReminderScheduleAt("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Schedule failed");
    }
    setReminderScheduling(false);
  };

  const cancelScheduledReminder = async (reminderId) => {
    if (!window.confirm("Cancel this scheduled reminder?")) return;
    try {
      await api.post(`/crm/scheduled-reminders/${reminderId}/cancel`);
      toast.success("Scheduled reminder cancelled");
      const r = await api.get(`/crm/leads/${leadId}/scheduled-reminders`);
      setScheduledReminders(r.data || []);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
  };

  const sendReminderTest = async () => {
    if (reminderTesting) return;
    setReminderTesting(true);
    try {
      const r = await api.post(`/crm/leads/${leadId}/send-manual-reminder/test`, {
        subject: reminderSubject.trim() || undefined,
        body_html: reminderBody.trim() || undefined,
      });
      toast.success(`Test sent to ${r.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    }
    setReminderTesting(false);
  };

  if (loading) return <div className="p-6"><div className="h-4 bg-[#F5F5F5] animate-pulse w-48" /></div>;
  if (!data) {
    return (
      <div className="p-6" data-testid="lead-detail-error">
        <p className="text-[#0A0A0A] font-semibold mb-1">
          {loadError?.status === 404 ? "Lead not found" : loadError?.status === 403 ? "Access denied" : "Couldn't load lead"}
        </p>
        <p className="text-[#A3A3A3] text-sm mb-4">
          {loadError?.message || "This lead could not be loaded."}
        </p>
        <button
          onClick={() => navigate("/crm/leads")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] hover:bg-[#F5F5F5]"
          data-testid="lead-not-found-back-btn"
        >
          ← Back to Leads
        </button>
      </div>
    );
  }
  const { lead, notes, events, email_log, meetings } = data;
  const f = editing ? form : lead;

  const lastInteraction = lead.last_interaction_date ? formatLocalDate(lead.last_interaction_date) : "-";

  const restoreLead = async () => {
    if (!window.confirm("Restore this archived lead to the active CRM?\n\nIt is NOT re-enrolled in marketing. "
      + "If its email address was removed as undeliverable, that address stays suppressed: update the email first.")) return;
    try {
      const r = await api.post("/admin/marketing/leads/bulk-restore", { lead_ids: [lead.lead_id] });
      if (r.data?.restored) toast.success("Lead restored. Update its email address if the old one was undeliverable.");
      else toast.warning("The lead was not archived any more; nothing was changed.");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(`Lead was not restored. ${typeof d === "string" ? d : "The server rejected the request."}`);
    }
  };

  return (
    <div className="p-6" data-testid="lead-detail-page">
      {lead.archived_at && (
        <div className="flex flex-wrap items-center gap-2 mb-4 px-3 py-2 border border-[#E5E5E5] bg-[#FAFAFA] text-xs text-[#525252]" role="note" data-testid="lead-archived-banner">
          <span className="font-bold uppercase tracking-wider text-[10px] text-[#0A0A0A]">Archived</span>
          <span>
            {formatLocalDate(lead.archived_at)}{lead.archived_by ? ` by ${lead.archived_by}` : ""}
            {lead.archive_reason ? ` — ${lead.archive_reason}` : ""}. Not in the active pipeline or marketing; history is kept.
          </span>
          {isAdmin && (
            <button onClick={restoreLead} className="ml-auto px-2 py-1 border border-[#0055FF] text-[#0055FF] font-bold uppercase tracking-wider text-[10px]" data-testid="lead-restore-btn">
              Restore lead
            </button>
          )}
        </div>
      )}
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <Link to="/crm/leads" className="inline-flex items-center gap-1.5 text-sm text-[#A3A3A3] hover:text-[#0A0A0A]"><ArrowLeft size={15} /> Back to Leads</Link>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50" data-testid="save-lead-btn"><FloppyDisk size={12} weight="bold" /> {saving ? "Saving..." : "Save"}</button>
              <button onClick={() => { setEditing(false); setForm(lead); }} className="px-3 py-1.5 text-xs border border-[#E5E5E5] text-[#525252] font-bold uppercase tracking-wider"><X size={12} /> Cancel</button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#0055FF] text-[#0055FF] text-xs font-bold uppercase tracking-wider hover:bg-[#F0F7FF]" data-testid="edit-lead-btn"><PencilSimple size={12} weight="bold" /> Edit</button>
          )}
          {!editing && lead.is_active !== false && lead.lead_status !== "closed_won" && (
            <button
              onClick={togglePause}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${lead.is_paused ? "border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]" : "border-[#D97706] text-[#D97706] hover:bg-[#FFFBEB]"}`}
              title={lead.is_paused ? "Resume sending emails to this lead" : "Pause — stop all chase emails for this lead"}
              data-testid="toggle-pause-btn"
            >
              {lead.is_paused ? "Resume" : "Pause"}
            </button>
          )}
          {!editing && (lead.lead_status || "").toLowerCase() === "invalid_email" && (
            <button
              onClick={revalidateEmail}
              disabled={revalidating}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#0EA5E9] text-[#0EA5E9] text-xs font-bold uppercase tracking-wider hover:bg-[#F0F9FF] disabled:opacity-50"
              title="Re-check this email's domain against 3 DNS resolvers. If the domain has working MX, the invalid_email flag is cleared."
              data-testid="revalidate-email-btn"
            >
              {revalidating ? "Checking…" : "Re-validate Email"}
            </button>
          )}
          {!editing && lead.is_active !== false && lead.lead_status !== "closed_won" && (
            <button onClick={promoteToClient} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#16A34A] text-[#16A34A] text-xs font-bold uppercase tracking-wider hover:bg-[#F0FDF4]" data-testid="promote-lead-btn"><CheckCircle size={12} weight="bold" /> Promote to Client</button>
          )}
          {!editing && !lead.deletion_requested && (
            <button onClick={markReplied} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#D97706] text-[#D97706] text-xs font-bold uppercase tracking-wider hover:bg-[#FFFBEB]" data-testid="mark-replied-btn" title="Pause active workflows for this lead"><ChatCircle size={12} weight="bold" /> Mark Replied</button>
          )}
          {!editing && (
            <button
              onClick={toggleBooked}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-xs font-bold uppercase tracking-wider ${
                lead.demo_booked_date
                  ? "border-[#0055FF] text-[#0055FF] hover:bg-[#EFF6FF]"
                  : "border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]"
              }`}
              data-testid="mark-booked-btn"
              title={lead.demo_booked_date
                ? "Lead has booked. Click to clear the booking flag (re-enables engagement follow-ups)."
                : "Mark as booked. Suppresses automatic engagement follow-up emails for this lead."}
            >
              <CheckCircle size={12} weight="bold" />
              {lead.demo_booked_date ? "Booked" : "Mark as Booked"}
            </button>
          )}
          {!editing && lead.is_active !== false && lead.lead_status !== "closed_won" && lead.lead_category !== "Closed Lost" && (
            <button
              onClick={openReminderModal}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#0055FF] text-[#0055FF] text-xs font-bold uppercase tracking-wider hover:bg-[#F0F7FF]"
              data-testid="lead-send-reminder-btn"
              title="Send a one-off 'just checking in' email. Updates the lead's last-action date and resumes any paused drip workflows."
            >
              <PaperPlaneTilt size={12} weight="bold" /> Send Reminder
            </button>
          )}
          {!editing && lead.is_active !== false && lead.lead_status !== "closed_won" && lead.lead_category !== "Closed Lost" && (
            <button
              onClick={markLost}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#DC2626] text-[#DC2626] text-xs font-bold uppercase tracking-wider hover:bg-[#FEF2F2]"
              data-testid="mark-lost-btn"
              title="Mark this lead as Lost — sets category to 'Closed Lost', pauses outbound emails, logs the event."
            >
              <X size={12} weight="bold" /> Mark as Lost
            </button>
          )}
          {!editing && (!lead.deletion_requested || isAdmin) && (
            <button
              onClick={requestDelete}
              disabled={deleting}
              className="px-3 py-1.5 text-xs font-bold text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50"
              data-testid="request-delete-btn"
              title={isAdmin ? "Permanently delete this lead and all related records" : "Submit a deletion request — platform admin must approve"}
            >
              {deleting ? "..." : (isAdmin ? "Delete" : "Request Deletion")}
            </button>
          )}
          {lead.deletion_requested && !isAdmin && <span className="px-2 py-1 bg-red-50 text-red-600 text-xs font-bold border border-red-200">Deletion Requested</span>}
        </div>
      </div>

      {/* Title bar */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 bg-[#F0F7FF] flex items-center justify-center flex-shrink-0">
          <span className="text-[#0055FF] font-black text-base">{lead.company_name?.[0]}</span>
        </div>
        <div>
          <div className="flex items-baseline gap-2 flex-wrap">
            {lead.lead_ref && (
              <span
                className="font-mono text-[11px] font-bold tracking-tight text-[#525252] bg-[#F5F5F5] border border-[#E5E5E5] px-1.5 py-0.5 whitespace-nowrap"
                title="Lead reference number — use this when calling the team about this lead"
                data-testid="lead-ref-pill"
              >
                {lead.lead_ref}
              </span>
            )}
            <h1 className="text-xl font-black text-[#0A0A0A] tracking-tight">{lead.company_name}</h1>
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={`px-2 py-0.5 text-xs font-bold border ${CAT_COLORS[lead.lead_category] || "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]"}`}>{lead.lead_category}</span>
            <span className={`px-2 py-0.5 text-xs font-bold ${lead.is_active ? "bg-emerald-50 text-emerald-700" : "bg-[#F5F5F5] text-[#A3A3A3]"}`}>{lead.is_active ? "Active" : "Inactive"}</span>
            {lead.is_paused && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300"
                title={lead.pause_reason || "Lead is paused — no emails being sent"}
                data-testid="lead-paused-badge"
              >
                ⏸ PAUSED
              </span>
            )}
            <span className="text-[10px] text-[#A3A3A3]">Last updated: {lastInteraction}</span>
          </div>
        </div>
      </div>

      <MarketingLifecyclePanel leadId={leadId} refreshKey={events?.length || 0} />

      {/* Session 73 — booking-check banner. Surfaces when the lead has
          one or more pending "Did they book?" prompts (auto-paused the
          drip on their meeting-link click). Yes confirms the booking
          and keeps them paused; No resumes the drip from the same step. */}
      {bookingChecks.filter((c) => c.status === "pending").length > 0 && (
        <div
          className="mb-5 border-2 border-[#0055FF] bg-[#F0F7FF] p-4"
          data-testid="booking-check-banner"
        >
          {bookingChecks
            .filter((c) => c.status === "pending")
            .map((c) => (
              <div key={c.check_id} className="space-y-2" data-testid={`booking-check-${c.check_id}`}>
                <div className="flex items-start gap-2">
                  <span className="text-xl leading-none">📞</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black text-[#0A0A0A] tracking-tight">
                      Did they actually schedule a meeting?
                    </p>
                    <p className="text-xs text-[#525252] mt-0.5">
                      {c.contact_first_name || "This lead"} clicked the booking link
                      {c.click_at
                        ? ` on ${formatLocal(c.click_at)}`
                        : ""}
                      . We paused the drip campaign at step {c.paused_step_index} so we
                      don't keep emailing them — confirm below.
                    </p>
                    {c.click_url && (
                      <p className="text-[10px] text-[#A3A3A3] mt-1 truncate" title={c.click_url}>
                        Link: {c.click_url}
                      </p>
                    )}
                    <p className="text-[10px] text-[#A3A3A3] mt-1">
                      If no decision by {c.auto_resume_at ? formatLocal(c.auto_resume_at) : "the auto-resume window"},
                      the system will auto-resume the drip (default-safe behaviour).
                    </p>
                    <details className="mt-2" data-testid={`booking-check-help-${c.check_id}`}>
                      <summary className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#0055FF] cursor-pointer select-none hover:underline">
                        How this works
                      </summary>
                      <ol className="mt-2 pl-4 space-y-1 text-[11px] text-[#525252] list-decimal">
                        <li>The lead clicked the Zoom-scheduler link in one of our drip emails.</li>
                        <li>We <strong>paused their drip</strong> right away so we don't pester them while they're booking.</li>
                        <li><strong className="text-[#15803D]">Yes — they booked</strong>: we set <code className="text-[10px] bg-white px-1 py-0.5 border border-[#E5E5E5]">demo_booked_date</code> and keep them out of the drip permanently.</li>
                        <li><strong className="text-[#D97706]">No — resume drip</strong>: we put them back in the drip at the exact same step they paused at.</li>
                        <li>If you don't decide by the auto-resume date, the system <strong>auto-resumes</strong> the drip as a safety net.</li>
                      </ol>
                      <p className="mt-2 text-[10px] text-[#A3A3A3]">
                        See all pending check-ins in the <Link to="/crm/booking-checks" className="text-[#0055FF] hover:underline">Booking Check-ins inbox →</Link>
                      </p>
                    </details>
                  </div>
                </div>
                <div className="flex gap-2 ml-7">
                  <button
                    onClick={() => decideBookingCheck(c.check_id, "booked")}
                    disabled={bookingDecisionInFlight}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#15803D] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#166534] disabled:opacity-50"
                    data-testid={`booking-check-yes-${c.check_id}`}
                  >
                    <CheckCircle size={12} weight="bold" /> Yes — they booked
                  </button>
                  <button
                    onClick={() => decideBookingCheck(c.check_id, "not_booked")}
                    disabled={bookingDecisionInFlight}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#D97706] text-[#D97706] text-xs font-bold uppercase tracking-wider hover:bg-[#FFFBEB] disabled:opacity-50"
                    data-testid={`booking-check-no-${c.check_id}`}
                  >
                    <X size={12} weight="bold" /> No — resume drip
                  </button>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Two-column layout (Session 1.14b): main content (tabs + form
          fields + inbound message) on the left; an ALWAYS-VISIBLE Event
          Log pinned to the right rail at lg+ widths. Stacks vertically
          on smaller screens. The Event Log moved out of the tab bar
          because reps want to see activity context without clicking. */}
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-4 items-start">
        <div className="min-w-0">

      {/* Tabs — Event Log was extracted into the right rail in 1.14b, so
          this row carries Notes / Meetings / Email Status / Workflows.
          Tab bar stays directly under the lead header so reps land on
          activity context first. */}
      <div className="bg-white border border-[#E5E5E5] overflow-hidden mb-4" data-testid="lead-tabs-top">
        <div className="flex border-b border-[#E5E5E5] overflow-x-auto">
          <TabBtn id="notes" label="Notes" active={activeTab === "notes"} onClick={setActiveTab} count={notes?.length} />
          <TabBtn id="lifecycle" label="Lifecycle" active={activeTab === "lifecycle"} onClick={setActiveTab} />
          <TabBtn id="meetings" label="Meetings & Transcripts" active={activeTab === "meetings"} onClick={setActiveTab} count={meetings?.length} />
          <TabBtn id="email_log" label="Email Status" active={activeTab === "email_log"} onClick={setActiveTab} count={email_log?.length} />
          <TabBtn id="marketing_emails" label="Marketing Sends" active={activeTab === "marketing_emails"} onClick={setActiveTab} />
          <TabBtn id="workflows" label="Workflows" active={activeTab === "workflows"} onClick={setActiveTab} />
          <TabBtn id="externally_sent_emails" label="Externally Sent Emails" active={activeTab === "externally_sent_emails"} onClick={setActiveTab} />
        </div>
        {activeTab === "notes" && <NotesTab leadId={leadId} notes={notes} onRefresh={load} />}
        {activeTab === "lifecycle" && <LifecycleTab lead={lead} events={events} />}
        {activeTab === "meetings" && <MeetingsTab leadId={leadId} meetings={meetings} onRefresh={load} />}
        {activeTab === "email_log" && <EmailLogTab leadId={leadId} emails={email_log} onRefresh={load} />}
        {activeTab === "marketing_emails" && <MarketingSendsTab leadId={leadId} />}
        {activeTab === "workflows" && <WorkflowsTab leadId={leadId} onRefreshEvents={load} />}
        {activeTab === "externally_sent_emails" && <ExternallySentEmailsTab entityType="lead" entityId={leadId} bccAddress={lead.bcc_address || null} />}
      </div>

      {/* Top half — fixed two-column layout (identical in view & edit) */}
      <div className="bg-white border border-[#E5E5E5] mb-5">
        <div className="grid grid-cols-[1fr_1fr] divide-x divide-[#F5F5F5]">
          {/* LEFT COLUMN: Company, Contact, Email, Address Lookup, Address, Dates */}
          <div className="divide-y divide-[#F5F5F5]">
            <Field label="Company" value={f.company_name} field="company_name" editing={editing} form={form} set={set} />
            {/* Contact split — Session 67. View-only mode shows the
                combined name (the legacy `contact_person`). Edit mode
                shows two inputs side-by-side; the backend rebuilds
                contact_person on save. */}
            {editing ? (
              <div className="px-3 py-2 grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#A3A3A3] uppercase tracking-wider font-bold block mb-1">First Name</label>
                  <input
                    value={form.contact_first_name || ""}
                    onChange={e => set("contact_first_name", e.target.value)}
                    className={inputCls}
                    data-testid="lead-contact-first-name"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#A3A3A3] uppercase tracking-wider font-bold block mb-1">Last Name</label>
                  <input
                    value={form.contact_last_name || ""}
                    onChange={e => set("contact_last_name", e.target.value)}
                    className={inputCls}
                    data-testid="lead-contact-last-name"
                  />
                </div>
              </div>
            ) : (
              <Field
                label="Contact"
                value={
                  // Prefer first+last when present, fall back to legacy contact_person.
                  (((f.contact_first_name || "").trim() + " " + (f.contact_last_name || "").trim()).trim()
                   || f.contact_person || "")
                }
                field="contact_person"
                editing={false}
                form={form}
                set={set}
              />
            )}
            <Field label="Email" value={f.contact_email} field="contact_email" type="email" editing={editing} form={form} set={set} />
            {/* Address lookup — only in US/UK or blank country */}
            {editing && (!form.country || /^(United States|USA|US|United Kingdom|UK|GB|England|Scotland|Wales)$/i.test(form.country || "")) && (
              <div className="px-3 py-2">
                <label className="text-[10px] text-[#A3A3A3] uppercase tracking-wider font-bold block mb-1">Address Lookup (US &amp; UK)</label>
                <div className="relative">
                  <input value={addressLookup} onChange={e => onAddressInput(e.target.value)} placeholder="Start typing an address..." className={inputCls} data-testid="address-lookup-input" />
                  {lookingUp && <div className="absolute right-2 top-2"><div className="w-4 h-4 border-2 border-[#0055FF] border-t-transparent animate-spin rounded-full" /></div>}
                </div>
                {addressSuggestions.length > 0 && (
                  <div className="border border-[#E5E5E5] mt-1 max-h-40 overflow-y-auto bg-white shadow-sm">
                    {addressSuggestions.map((s, i) => (
                      <button key={i} onClick={() => applyAddress(s)} className="w-full text-left px-3 py-2 text-xs hover:bg-[#F0F7FF] border-b border-[#F5F5F5] last:border-0" data-testid={`address-suggestion-${i}`}>{s.display_name}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex">
              <div className={cellLabel + " !py-3"}>Address</div>
              <div className={cellValue + " flex-1 !py-3"}>
                {editing ? (
                  <div className="space-y-2">
                    <input value={form.address_line_1 ?? ""} onChange={e => set("address_line_1", e.target.value)} placeholder="Address Line 1" className={inputCls} data-testid="addr-line1" />
                    <input value={form.address_line_2 ?? ""} onChange={e => set("address_line_2", e.target.value)} placeholder="Address Line 2" className={inputCls} data-testid="addr-line2" />
                    <input value={form.city ?? ""} onChange={e => set("city", e.target.value)} placeholder="City" className={inputCls} data-testid="addr-city" />
                    <input value={form.state_region ?? ""} onChange={e => set("state_region", e.target.value)} placeholder="State / Region" className={inputCls} data-testid="addr-state" />
                    <input value={form.postal_code ?? ""} onChange={e => set("postal_code", e.target.value)} placeholder="Postal / Zip Code" className={inputCls} data-testid="addr-postal" />
                    <input value={form.country ?? ""} onChange={e => set("country", e.target.value)} placeholder="Country" className={inputCls} data-testid="addr-country" list="lead-country-suggestions" />
                    {/* Country dropdown — Session 67. US + UK pinned
                        to the top of the suggestion list because those
                        are by far the most common deployments; the
                        rest follow alphabetically. We use <datalist>
                        instead of <select> so the operator can still
                        type a free-form value (e.g. "United States of
                        America" or "U.S.A.") for legacy data. */}
                    <datalist id="lead-country-suggestions">
                      <option value="United States" />
                      <option value="United Kingdom" />
                      <option value="Australia" />
                      <option value="Canada" />
                      <option value="France" />
                      <option value="Germany" />
                      <option value="India" />
                      <option value="Ireland" />
                      <option value="Japan" />
                      <option value="Mexico" />
                      <option value="Netherlands" />
                      <option value="New Zealand" />
                      <option value="Singapore" />
                      <option value="South Africa" />
                      <option value="Spain" />
                      <option value="Sweden" />
                      <option value="Switzerland" />
                    </datalist>
                  </div>
                ) : (
                  <div className="text-sm text-[#0A0A0A] leading-relaxed">
                    {[f.address_line_1, f.address_line_2, f.city, f.state_region, f.postal_code, f.country].filter(Boolean).length > 0
                      ? [f.address_line_1, f.address_line_2, f.city, f.state_region, f.postal_code, f.country].filter(Boolean).map((line, i) => <div key={i}>{line}</div>)
                      : <span className="text-[#A3A3A3]">-</span>}
                  </div>
                )}
              </div>
            </div>
            <div className="flex">
              <div className={cellLabel}>Timezone</div>
              <div className={cellValue + " flex-1"}>
                {editing ? (
                  <TimezoneSelect
                    value={form.timezone}
                    onChange={(v) => set("timezone", v)}
                    testId="lead-timezone-select"
                  />
                ) : (
                  <span className={f.timezone ? "text-sm text-[#0A0A0A]" : "text-sm text-[#A3A3A3] italic"}>
                    {f.timezone || "— inferred from country/state —"}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Phone, Category, Lead Type, Ball With, Source, etc. */}
          <div className="divide-y divide-[#F5F5F5]">
            <Field label="Phone" value={f.phone_number} field="phone_number" editing={editing} form={form} set={set} />
            <Field label="Category" value={f.lead_category} field="lead_category" editing={editing} form={form} set={set} options={LEAD_CATEGORIES} />
            <Field label="Lead Type" value={f.lead_type} field="lead_type" editing={editing} form={form} set={set} options={["Active", "Dormant", "Converted"]} />
            <Field label="Ball With" value={f.ball_with} field="ball_with" editing={editing} form={form} set={set} options={["Eyvo", "Client", "Both"]} />
            <Field label="Source" value={f.source_of_lead} field="source_of_lead" editing={editing} form={form} set={set} options={["", ...SOURCES]} />
            <Field label="Assigned To" value={f.assigned_to} field="assigned_to" editing={editing} form={form} set={set} />
            <BccEmailField label="BCC to Pulse" address={lead.bcc_address} />
            <Field label="Last Updated By" value={lead.last_updated_by} />
            {editing && (
              <div className="flex items-center px-3 py-2">
                <div className={cellLabel}>Active</div>
                <div className="flex-1 px-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.is_active !== false} onChange={e => set("is_active", e.target.checked)} className="border-[#E5E5E5]" />
                    <span className="text-xs text-[#525252]">Active lead</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Date timeline — 4-up horizontal strip in the user's preferred order */}
        <div className="px-5 pb-2 pt-3 border-t border-[#F5F5F5] grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="lead-dates-strip">
          <DateCell label="Entry Date"      value={f.entry_date}          field="entry_date"          editing={editing} form={form} set={set} />
          <DateCell label="Discovery Date"  value={f.discovery_call_date} field="discovery_call_date" editing={editing} form={form} set={set} />
          <DateCell label="Demo Booked"     value={f.demo_booked_date}    field="demo_booked_date"    editing={editing} form={form} set={set} />
          <DateCell label="Demo Date"       value={f.demo_date}           field="demo_date"           editing={editing} form={form} set={set} />
        </div>

        {/* Modules Interested In — spans full width so long checkbox grids breathe */}
        <div className="px-5 pb-5 pt-2 border-t border-[#F5F5F5]" data-testid="lead-modules-panel">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Modules Interested In</div>
          {editing ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 border border-[#E5E5E5] p-3 bg-[#FAFAFA]">
              {LEAD_MODULES.map(m => {
                const selected = Array.isArray(form.modules_interested) && form.modules_interested.includes(m);
                return (
                  <label key={m} className="flex items-center gap-2 text-xs text-[#0A0A0A] cursor-pointer select-none hover:bg-white px-1 py-0.5">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => {
                        const cur = Array.isArray(form.modules_interested) ? form.modules_interested : [];
                        const next = cur.includes(m) ? cur.filter(x => x !== m) : [...cur, m];
                        set("modules_interested", next);
                      }}
                      className="w-3.5 h-3.5 accent-[#0055FF]"
                      data-testid={`detail-module-cb-${m.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                    />
                    <span>{m}</span>
                  </label>
                );
              })}
            </div>
          ) : (
            (Array.isArray(lead.modules_interested) && lead.modules_interested.length > 0) ? (
              <div className="flex flex-wrap gap-1.5">
                {lead.modules_interested.map(m => (
                  <span key={m} className="text-[11px] font-medium bg-[#EFF6FF] border border-[#0055FF] text-[#0055FF] px-2 py-0.5">
                    {m}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[#A3A3A3] italic">None selected</p>
            )
          )}
        </div>
      </div>

      {/* Original message submitted via the website form. Surfaces what the
          prospect actually wrote so reps can read it without digging into a
          tab. Hidden when the field is empty (manual leads usually). */}
      {lead.registration_notes && (
        <div className="bg-white border border-[#E5E5E5] mt-4" data-testid="lead-inbound-message">
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-[#E5E5E5] bg-[#FFFBEB]">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#92400E]">
              Inbound message from website form
            </p>
            {lead.source_of_lead && (
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#525252]">
                via {lead.source_of_lead}
              </p>
            )}
          </div>
          <div className="px-5 py-4 text-sm text-[#171717] whitespace-pre-wrap leading-relaxed font-normal" style={{ fontFamily: "Georgia, serif" }}>
            {lead.registration_notes}
          </div>
        </div>
      )}
        </div>{/* /left column */}

        {/* RIGHT RAIL — always-visible Event Log. Sticky on tall screens
            so it stays in view as the operator scrolls through long form
            fields on the left. Header has a subtle pulse dot so the rep
            can tell at a glance whether new events arrived since they
            opened the page (e.g. an open/click webhook fired). */}
        <aside className="mt-4 lg:mt-0 lg:sticky lg:top-4 self-start min-w-0" data-testid="lead-event-log-rail">
          <div className="bg-white border border-[#E5E5E5] overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E5E5] bg-[#FAFAFA]">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#525252]">
                Event Log
              </p>
              <span className="text-[10px] font-mono text-[#A3A3A3] tabular-nums" data-testid="lead-event-log-count">
                {events?.length ?? 0}
              </span>
            </div>
            <EventsTab events={events} />
          </div>
        </aside>
      </div>

      {/* Manual reminder modal — opens from the "Send Reminder" button
          in the page header. Pre-fills concrete personalised defaults
          that the operator can customise in a full WYSIWYG (TipTap)
          editor. Edit ↔ Preview toggle shows the rendered email; the
          "Send Test to Me" button delivers a copy to the operator's
          own inbox first so they can sanity-check before pulling the
          trigger on the customer-facing send. */}
      {showReminderModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="manual-reminder-modal">
          <div className="bg-white border border-[#E5E5E5] w-full max-w-4xl max-h-[90vh] flex flex-col shadow-xl">
            <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-[#FAFAFA] flex-shrink-0">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#0A0A0A]">Send Manual Reminder</p>
              <button
                onClick={() => setShowReminderModal(false)}
                className="text-[#A3A3A3] hover:text-[#0A0A0A] text-xs font-bold"
                data-testid="manual-reminder-close"
              >✕</button>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto flex-1">
              <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1 items-center text-sm">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">To</span>
                <span className="text-[#0A0A0A] font-mono" data-testid="manual-reminder-to">
                  {lead.contact_email || <span className="text-[#A3A3A3] italic">(no email on file)</span>}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">Subject</span>
                <input
                  type="text"
                  value={reminderSubject}
                  onChange={(e) => setReminderSubject(e.target.value)}
                  placeholder={`Following up on our recent conversation — ${lead.company_name}`}
                  className="w-full border border-[#E5E5E5] px-3 py-1.5 text-sm focus:outline-none focus:border-[#0055FF]"
                  data-testid="manual-reminder-subject"
                />
              </div>

              {/* Edit ↔ Preview tabs */}
              <div className="flex border-b border-[#E5E5E5]">
                <button
                  onClick={() => setReminderMode("edit")}
                  className={`px-4 py-2 text-[10px] font-bold uppercase tracking-[0.16em] border-b-2 ${reminderMode === "edit" ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#A3A3A3] hover:text-[#525252]"}`}
                  data-testid="manual-reminder-tab-edit"
                >Edit</button>
                <button
                  onClick={() => setReminderMode("preview")}
                  className={`px-4 py-2 text-[10px] font-bold uppercase tracking-[0.16em] border-b-2 ${reminderMode === "preview" ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#A3A3A3] hover:text-[#525252]"}`}
                  data-testid="manual-reminder-tab-preview"
                >Preview</button>
              </div>

              {reminderMode === "edit" && (
                <RichTextEditor
                  html={reminderBody}
                  onChange={setReminderBody}
                  placeholder="Compose the reminder email…"
                  minHeight={300}
                  testId="manual-reminder-editor"
                />
              )}

              {reminderMode === "preview" && (
                <div className="border border-[#E5E5E5] bg-white" data-testid="manual-reminder-preview">
                  <div className="bg-[#FAFAFA] border-b border-[#E5E5E5] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#525252]">
                    Preview · subject: <span className="font-normal normal-case text-[#0A0A0A]">{reminderSubject || <em>(no subject)</em>}</span>
                  </div>
                  <iframe
                    title="Reminder preview"
                    srcDoc={safeSrcDoc(`<!DOCTYPE html><html><head><style>body{font-family:Arial,sans-serif;color:#0A0A0A;margin:16px;line-height:1.5;font-size:14px;} p{margin:0 0 12px 0;} a{color:#0055FF;}</style></head><body>${reminderBody || "<em style='color:#A3A3A3'>(empty body)</em>"}</body></html>`)}
                    sandbox=""
                    className="w-full"
                    style={{ minHeight: 320, border: "none" }}
                  />
                </div>
              )}

              <div className="bg-[#F0F7FF] border border-[#0055FF]/20 px-3 py-2 text-[11px] text-[#525252] leading-snug">
                <strong>What happens when you send:</strong> the email is delivered immediately,
                the lead's <em>last action date</em> is stamped to now (resetting heat decay),
                and any paused workflow enrolments resume — the drip's next email will fire
                7 days from now. Logged on the lead's event timeline AND in Email Status.
                Use <strong>Send Test to Me</strong> first if you want to see it in your own inbox.
              </div>

              {/* Already-scheduled reminders for this lead — Session 67.
                  Shows a compact list with a Cancel button per row so
                  the operator doesn't double-schedule by accident. */}
              {(scheduledReminders || []).filter(r => r.status === "pending").length > 0 && (
                <div className="bg-amber-50 border border-amber-300 px-3 py-2 text-[11px] text-amber-900" data-testid="scheduled-reminders-banner">
                  <strong>{scheduledReminders.filter(r => r.status === "pending").length} reminder(s) already scheduled for this lead:</strong>
                  <ul className="mt-1 space-y-0.5">
                    {scheduledReminders.filter(r => r.status === "pending").map(r => {
                      const dt = new Date(r.scheduled_at);
                      const ptStr = new Intl.DateTimeFormat("en-US", {
                        timeZone: "America/Los_Angeles",
                        dateStyle: "medium", timeStyle: "short",
                      }).format(dt);
                      const tz = getPtAbbrev(dt);
                      return (
                        <li key={r.id} className="flex items-center justify-between gap-2">
                          <span className="truncate">
                            {ptStr} {tz} — <em>{r.subject}</em>
                          </span>
                          <button
                            onClick={() => cancelScheduledReminder(r.id)}
                            className="text-amber-800 hover:text-amber-950 underline text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
                            data-testid={`cancel-scheduled-${r.id}`}
                          >Cancel</button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-[#E5E5E5] flex items-center justify-between gap-2 bg-[#FAFAFA] flex-shrink-0 flex-wrap">
              <button
                onClick={sendReminderTest}
                disabled={reminderTesting || reminderSending || reminderScheduling}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider text-[#525252] hover:bg-white disabled:opacity-50"
                data-testid="manual-reminder-send-test"
                title="Sends the same email to YOUR address instead of the lead's. No state changes on the lead."
              >
                {reminderTesting ? "Testing…" : "Send Test to Me"}
              </button>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Schedule-send picker — Session 67. Inline date+time
                    input keeps the layout compact. We pin the picker to
                    America/Los_Angeles regardless of where the operator
                    sits, so a 9:00 AM scheduled send means 9:00 AM at
                    Eyvo HQ (the recipient's POV). The label shows the
                    current PT abbreviation (PST/PDT) and, once a value
                    is picked, the equivalent UTC moment so the rep can
                    sanity-check before clicking Schedule. */}
                <div className="flex flex-col gap-0.5" data-testid="manual-reminder-schedule-block">
                  <div className="flex items-center gap-1">
                    <input
                      type="datetime-local"
                      value={reminderScheduleAt}
                      onChange={e => setReminderScheduleAt(e.target.value)}
                      className="border border-[#E5E5E5] px-2 py-1 text-xs text-[#525252] focus:outline-none focus:border-[#0055FF]"
                      data-testid="manual-reminder-schedule-at"
                      title="Pick a future date/time (interpreted as Pacific Time)"
                    />
                    <span
                      className="px-1.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-[#F5F5F5] text-[#525252] border border-[#E5E5E5] whitespace-nowrap"
                      data-testid="manual-reminder-tz-label"
                      title="Pacific Time (America/Los_Angeles) — DST aware"
                    >
                      {getPtAbbrev(new Date())}
                    </span>
                  </div>
                  {reminderScheduleAt && (() => {
                    const iso = pacificLocalToUtcIso(reminderScheduleAt);
                    if (!iso) return null;
                    const dt = new Date(iso);
                    const utcEcho = new Intl.DateTimeFormat("en-US", {
                      timeZone: "UTC", dateStyle: "short", timeStyle: "short",
                    }).format(dt);
                    // Lead's local time — derived from address (city/state/
                    // country). Falls back to America/New_York (US Eastern)
                    // when the lead has no address. The label tells the
                    // operator which zone we picked + why.
                    const leadTzInfo = deriveLeadTimezone(lead || {});
                    const leadEcho = formatInTz(dt, leadTzInfo.tz);
                    const leadAbbr = getTzAbbrev(dt, leadTzInfo.tz);
                    // "Your time" — no `timeZone` option means the browser's
                    // own zone, which is exactly what the rep wants to see.
                    const localEcho = new Intl.DateTimeFormat("en-US", {
                      dateStyle: "short", timeStyle: "short",
                    }).format(dt);
                    const localTzAbbr = (
                      new Intl.DateTimeFormat("en-US", {
                        timeZoneName: "short",
                      }).formatToParts(dt).find(p => p.type === "timeZoneName")?.value
                      || ""
                    );
                    const localIana = Intl.DateTimeFormat()
                      .resolvedOptions().timeZone || "";
                    const isOnPt = localIana === "America/Los_Angeles";
                    const leadOnPt = leadTzInfo.tz === "America/Los_Angeles";
                    const leadOnLocal = leadTzInfo.tz === localIana;
                    return (
                      <>
                        <span
                          className="text-[9px] text-[#A3A3A3] font-mono whitespace-nowrap"
                          data-testid="manual-reminder-utc-echo"
                        >
                          = {utcEcho} UTC
                        </span>
                        {/* Lead's timezone echo — biggest accuracy win
                            of all three. The rep cares most about when
                            it lands in the *recipient's* inbox. */}
                        {!leadOnPt && (
                          <span
                            className="text-[9px] text-emerald-700 font-mono whitespace-nowrap font-bold"
                            data-testid="manual-reminder-lead-echo"
                            title={`Lead's timezone: ${leadTzInfo.tz} (${leadTzInfo.label})`}
                          >
                            = {leadEcho} {leadAbbr} (lead{leadTzInfo.source === "default" ? " — default" : ""})
                          </span>
                        )}
                        {!isOnPt && !leadOnLocal && (
                          <span
                            className="text-[9px] text-[#0055FF] font-mono whitespace-nowrap"
                            data-testid="manual-reminder-local-echo"
                            title={`Your browser timezone: ${localIana}`}
                          >
                            = {localEcho} {localTzAbbr} (your time)
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
                <button
                  onClick={scheduleManualReminder}
                  disabled={reminderScheduling || reminderSending || reminderTesting || !reminderScheduleAt || !lead.contact_email}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#0055FF] text-[#0055FF] text-xs font-bold uppercase tracking-wider hover:bg-[#F0F7FF] disabled:opacity-50"
                  data-testid="manual-reminder-schedule"
                  title={reminderScheduleAt ? "Queue this reminder for the selected Pacific date/time" : "Pick a date/time to enable"}
                >
                  {reminderScheduling ? "Scheduling…" : "Schedule Send"}
                </button>
                <button
                  onClick={() => setShowReminderModal(false)}
                  disabled={reminderSending || reminderTesting || reminderScheduling}
                  className="px-3 py-1.5 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider text-[#525252] hover:bg-white disabled:opacity-50"
                  data-testid="manual-reminder-cancel"
                >Cancel</button>
                <button
                  onClick={sendManualReminder}
                  disabled={reminderSending || reminderTesting || reminderScheduling || !lead.contact_email}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50"
                  data-testid="manual-reminder-send"
                >
                  <PaperPlaneTilt size={11} weight="bold" /> {reminderSending ? "Sending…" : "Send Now"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/** Shows the workflows this lead is currently (or was previously) enrolled
 *  in. Includes an "Enroll in Workflow" dropdown listing every ACTIVE
 *  workflow, plus an Unenroll action for rows whose status is 'active'.
 *  Enrollment data comes from `/api/crm/leads/:id/workflow-enrollments`
 *  which resolves lead → contact → enrollments, so brand-new leads with
 *  no email yet (or no matching mkt_contact) simply show an empty list. */
function WorkflowsTab({ leadId, onRefreshEvents }) {
  const [enrollments, setEnrollments] = useState([]);
  const [activeWorkflows, setActiveWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [e, w] = await Promise.all([
        api.get(`/crm/leads/${leadId}/workflow-enrollments`),
        api.get(`/marketing/workflows`),
      ]);
      setEnrollments(Array.isArray(e.data) ? e.data : []);
      const list = Array.isArray(w.data) ? w.data : (w.data?.workflows || []);
      setActiveWorkflows(list.filter((x) => x.status === "active"));
    } catch (err) {
      console.error("Failed to load workflow enrollments", err);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  const enroll = async (workflowId, workflowName) => {
    setBusy(true);
    setShowPicker(false);
    try {
      const r = await api.post(`/crm/leads/${leadId}/enroll-workflow`, { workflow_id: workflowId });
      if (r.data?.enrolled) {
        toast.success(`Enrolled in '${workflowName}'`);
      } else if (r.data?.reason === "already_enrolled") {
        toast.info(`Already enrolled in '${workflowName}'`);
      }
      await load();
      onRefreshEvents?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enrollment failed");
    }
    setBusy(false);
  };

  const unenroll = async (enrollmentId, workflowName) => {
    if (!window.confirm(`Stop this lead's enrollment in '${workflowName}'? Scheduled emails will be cancelled.`)) return;
    setBusy(true);
    try {
      await api.post(`/crm/leads/${leadId}/enrollments/${enrollmentId}/unenroll`, {});
      toast.success("Enrollment stopped");
      await load();
      onRefreshEvents?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not stop enrollment");
    }
    setBusy(false);
  };

  const activeEnrollmentIds = new Set(
    enrollments.filter((e) => e.status === "active").map((e) => e.workflow_id),
  );
  // Show ALL active workflows in the picker. Workflows the lead is
  // already enrolled in render as "Already enrolled" and aren't
  // clickable — this is clearer than silently hiding them and then
  // disabling the top-level button with no explanation, which reads
  // as "the feature is broken" from the user's POV.
  return (
    <div className="p-5" data-testid="lead-workflows-tab">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">Marketing automation</p>
          <h3 className="text-base font-black tracking-tight text-[#0A0A0A] mt-0.5"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Workflow enrollments
          </h3>
          <p className="text-[11px] text-[#525252] mt-1">
            Workflows automatically drip-email this lead on a schedule. Enroll here to start a sequence; unenroll to stop it.
          </p>
        </div>
        <div className="relative">
          <button
            type="button"
            disabled={busy}
            onClick={() => setShowPicker((v) => !v)}
            className="h-9 px-3 text-[11px] font-bold uppercase tracking-wider bg-[#0A0A0A] text-white hover:bg-[#262626] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            data-testid="enroll-workflow-btn"
          >
            <Plus size={11} weight="bold" />
            Enroll in Workflow
          </button>
          {showPicker && (
            <div className="absolute right-0 top-10 z-20 bg-white border border-[#E5E5E5] shadow-lg min-w-[300px] max-w-sm"
                 data-testid="workflow-picker">
              <div className="px-3 py-2 border-b border-[#F5F5F5] flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                  Active workflows {activeWorkflows.length > 0 ? `(${activeWorkflows.length})` : ""}
                </p>
                <button type="button" onClick={() => setShowPicker(false)}
                        className="text-[#A3A3A3] hover:text-[#525252]">
                  <X size={11} />
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {activeWorkflows.length === 0 ? (
                  <div className="px-3 py-4">
                    <p className="text-[11px] text-[#0A0A0A] font-bold">No active workflows yet.</p>
                    <p className="text-[11px] text-[#525252] mt-1 leading-relaxed">
                      Go to <strong>Marketing → Workflows</strong>, open the one you want to use, and click <strong>Approve</strong> / <strong>Activate</strong>. It'll appear here instantly.
                    </p>
                  </div>
                ) : (
                  activeWorkflows.map((w) => {
                    const alreadyEnrolled = activeEnrollmentIds.has(w.id);
                    return (
                      <button
                        key={w.id}
                        type="button"
                        disabled={alreadyEnrolled || busy}
                        onClick={() => !alreadyEnrolled && enroll(w.id, w.name)}
                        className={`w-full text-left px-3 py-2.5 border-b border-[#F5F5F5] last:border-b-0 ${
                          alreadyEnrolled
                            ? "bg-[#FAFAFA] cursor-not-allowed"
                            : "hover:bg-[#EEF2FA]"
                        }`}
                        data-testid={`enroll-option-${w.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-xs font-bold truncate ${alreadyEnrolled ? "text-[#A3A3A3]" : "text-[#0A0A0A]"}`}>
                            {w.name}
                          </p>
                          {alreadyEnrolled && (
                            <span className="text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3] whitespace-nowrap">
                              already enrolled
                            </span>
                          )}
                        </div>
                        {w.description && (
                          <p className={`text-[10px] truncate mt-0.5 ${alreadyEnrolled ? "text-[#A3A3A3]" : "text-[#525252]"}`}>
                            {w.description}
                          </p>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : enrollments.length === 0 ? (
        <div className="border border-dashed border-[#E5E5E5] p-8 text-center">
          <p className="text-[12px] text-[#525252]">
            Not enrolled in any workflows yet.
          </p>
          {activeWorkflows.length === 0 && (
            <p className="text-[11px] text-[#A3A3A3] mt-2">
              No active workflows available. Go to Marketing → Workflows and approve one first.
            </p>
          )}
          {activeWorkflows.length > 0 && (
            <p className="text-[11px] text-[#A3A3A3] mt-2">
              Click <strong>Enroll in Workflow</strong> above to start a drip sequence.
            </p>
          )}
        </div>
      ) : (
        <div className="border border-[#E5E5E5]">
          <div className="grid grid-cols-[1fr_90px_100px_160px_80px] gap-3 items-center px-3 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5] text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
            <span>Workflow</span>
            <span>Status</span>
            <span className="text-right">Step</span>
            <span className="text-right">Enrolled / Next</span>
            <span></span>
          </div>
          {enrollments.map((e) => (
            <div
              key={e.enrollment_id}
              className="grid grid-cols-[1fr_90px_100px_160px_80px] gap-3 items-center px-3 py-3 border-b border-[#F5F5F5] last:border-b-0"
              data-testid={`enrollment-row-${e.enrollment_id}`}
            >
              <div className="min-w-0">
                <p className="text-xs font-bold text-[#0A0A0A] truncate">{e.workflow_name}</p>
                <p className="text-[10px] text-[#A3A3A3] truncate">wf: {e.workflow_id}</p>
              </div>
              <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider w-fit ${
                e.status === "active"    ? "bg-[#ECFDF5] text-[#047857] border border-[#A7F3D0]" :
                e.status === "paused"    ? "bg-[#FEF3C7] text-[#92400E] border border-[#FCD34D]" :
                e.status === "completed" ? "bg-[#F5F5F5] text-[#525252] border border-[#E5E5E5]" :
                                           "bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA]"
              }`}>
                {e.status}
              </span>
              <span className="text-right text-xs tabular-nums text-[#525252]">
                #{(e.current_step_index ?? 0) + 1}
              </span>
              <span className="text-right text-[10px] tabular-nums text-[#A3A3A3] leading-tight">
                {e.enrolled_at && <div>enrolled {formatShortRel(e.enrolled_at)}</div>}
                {e.status === "active" && e.next_action_at && <div className="text-[#0055FF]">next {formatShortRel(e.next_action_at)}</div>}
                {e.completed_at && <div>finished {formatShortRel(e.completed_at)}</div>}
              </span>
              <div className="text-right">
                {e.status === "active" && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => unenroll(e.enrollment_id, e.workflow_name)}
                    className="text-[10px] font-bold uppercase tracking-wider text-[#991B1B] hover:underline disabled:opacity-40"
                    data-testid={`unenroll-${e.enrollment_id}`}
                  >
                    Stop
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatShortRel(iso) {
  if (!iso) return "";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "";
  const mins = Math.floor((t.getTime() - Date.now()) / 60000);
  const absM = Math.abs(mins);
  const sign = mins >= 0 ? "in " : "";
  const suffix = mins >= 0 ? "" : " ago";
  if (absM < 60)    return `${sign}${absM}m${suffix}`;
  if (absM < 1440)  return `${sign}${Math.floor(absM / 60)}h${suffix}`;
  if (absM < 10080) return `${sign}${Math.floor(absM / 1440)}d${suffix}`;
  return t.toLocaleDateString();
}

